/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SWModules.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";


import appTheme from "../../assets/AppTheme/appTheme";
import customConsole from "../../config/customConsole";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  adminSettingsAPI,
  HandleApiActions,
  labCar,
} from "../../redux/actions";
import { useNavigate } from "react-router-dom";
import editRequestAPI from "../../redux/actions/editRequestApi";
import LockIcon from "@mui/icons-material/Lock";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";
import moment from "moment";
import actionType from "../../redux/actionTypes";
import GithubSWMLogs from "./GithubSWMLogs";
import githubIntegrationAction from "../../redux/actions/githubIntegrationAction";
import typeApprovalAPI from "../../redux/actions/typeApprovalAPI";
import { archiveReasons, PROJECT_STATUS } from "../../config/appConstants";
import ruleEngineActions from "../../redux/actions/ruleEngineAction";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import { Search } from "@mui/icons-material";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";

import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import { componentStyle } from "../../config/appTheme";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomCheckBoxV2 from "../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../components/customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";
import EditDetailsDialog from "../../components/customComponentsV2/vlc/pages/EditDetailsDialog";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";
import RequestsAndLogsTabs from "./requestAccess";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import RecreateSWMDialog from "../../components/customComponentsV2/vlc/pages/RecreateSWMDialog";
import DecisionMatrixPanel from "../legal-requirements/RuleCenter/Executions/DecisionMatrixPanel";
import { showToastMessage } from "../../redux/actions/actionHandler";
import AttributesForm from "./Attributes/AttributesForm";
import AttributeUpdateDialog from "./Attributes/AttributeUpdateDialog";

const getGithubLinkError = (value) => {
  const trimmedValue = value?.trim() || "";

  if (!trimmedValue) return "";

  try {
    const parsedUrl = new URL(trimmedValue);
    const isHttpUrl = ["http:", "https:"].includes(parsedUrl.protocol);

    return isHttpUrl
      ? ""
      : "Enter a valid URL, for example https://github.com/username/repo";
  } catch {
    return "Enter a valid URL, for example https://github.com/username/repo";
  }
};

const isGithubLinkValid = (value) => getGithubLinkError(value) === "";

const getGithubLinkPayload = (value) => {
  const trimmedValue = value?.trim() || "";

  return trimmedValue && isGithubLinkValid(trimmedValue)
    ? { git_link: trimmedValue }
    : null;
};

const isDecisionMatrixContextValid = (data) => {
  if (!data?.standards || data.standards.length === 0) {
    return false;
  }

  return data.standards.some((standard) => {
    if (!standard.rule_sets || standard.rule_sets.length === 0) {
      return false;
    }

    return standard.rule_sets.some(
      (ruleSet) => ruleSet.workflows && ruleSet.workflows.length > 0,
    );
  });
};

const DetailRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={1}>
    <Typography
      variant="subtitle2"
      fontWeight={600}
      sx={{ minWidth: "130px", color: "#333" }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
    >
      {value}
    </Typography>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
};

function VlcSWModules({
  selectedVariantsMany,
  variantOptionList,
  heading,
  sWModulesList,
  handleSWModuleAdd,
  handleSWModuleUpdate,
  handleSWModuleReplace,
  setIsEEComponentSelected,
  setSelectedEEComponentDetails,
  selectedProjectDetails,
  selectedEEComponentDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  getSWModulesList,
  selectedSingleVariantDetails,
  swmSearchFieldValue,
  setSWMSearchFieldValue,
  swmSelectedSearchField,
  setSWMSelectedSearchField,
  page,
  setPage,
  rowsPerPage,
  totalSWMCount,
  onRedirectedActionDialogClose,
  viewArchived,
  selectedArchivedSWMs,
  setSelectedArchivedSWMs,
  selectedArchivedEEs,
  clearAllCounter,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const projectDetails = selectedProjectDetails || reduxProjectDetails;
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
    shallowEqual,
  );

  const profileDetails = useSelector((store) => store.profileReducer);

  const roleType = profileDetails?.profile?.role_details?.role_type;

  const supplier = roleType === 2;

  const canEditItem = (item) =>
    item?.is_edit_enabled || selectedProjectDetails?.is_user_project_owner;

  const getActionTooltipTitle = (actionType) => {
    switch (actionType) {
      case 1:
        return "Action required in Software Module to Software Module linking";
      case 2:
        return "Action required in Software Module to Legal requirements";
      case 3:
        return "Action required in this Software module";
      case 4:
        return "Action required in Software Module Attribute";
      case 5:
        return "Action pending in Software Module Version";
      default:
        return "";
    }
  };

  const checkIsParentEENotActive = (selectedEEComponentDetails, item) => {
    const isStatusArchived = (val) =>
      typeof val === "string" && val.toUpperCase() === "ARCHIVED";

    if (
      isStatusArchived(selectedEEComponentDetails?.archive_status) ||
      isStatusArchived(selectedEEComponentDetails?.archive_reason) ||
      isStatusArchived(item?.ee_details?.archive_status) ||
      isStatusArchived(item?.ee_archive_status)
    ) {
      return true;
    }

    if (
      Array.isArray(selectedEEComponentDetails?.archive_reason) &&
      selectedEEComponentDetails.archive_reason.some(
        (r) => typeof r === "string" && r.toUpperCase() === "ARCHIVED",
      )
    ) {
      return true;
    }

    if (
      typeof selectedEEComponentDetails?.status === "string" &&
      ["ARCHIVED", "INACTIVE"].includes(
        selectedEEComponentDetails.status.toUpperCase(),
      )
    ) {
      return true;
    }

    if (selectedEEComponentDetails?.is_active === false) {
      return true;
    }

    if (
      Array.isArray(item?.archive_reason) &&
      item.archive_reason.some((r) => {
        const str = String(r).toUpperCase();
        return (
          str.includes("EE_ARCHIVED") ||
          str.includes("EE_COMPONENT_ARCHIVED")
        );
      })
    ) {
      return true;
    }

    return false;
  };

  const getSWMArchiveTooltipTitle = (missingArchiveReasons, isParentEENotActive) => {
    const displayMissingReasons = missingArchiveReasons.filter(
      (r) =>
        !String(r).toUpperCase().includes("EE_ARCHIVED") &&
        !String(r).toUpperCase().includes("EE_COMPONENT_ARCHIVED"),
    );
    let displayReasons = [];
    if (displayMissingReasons.length > 0) {
      displayReasons = displayMissingReasons;
    } else if (!isParentEENotActive && missingArchiveReasons.length > 0) {
      displayReasons = missingArchiveReasons;
    }

    return [
      displayReasons.length > 0 ? `Missing ${displayReasons.join(", ")}` : "",
      isParentEENotActive ? "Parent EE component is not active" : "",
    ]
      .filter(Boolean)
      .join(", ");
  };

  const renderSWMArchiveErrorIcon = (item) => {
    const hasMissingArchiveReasons =
      Array.isArray(item?.archive_reason) &&
      item.archive_reason.some(
        (r) => !archiveReasons?.swm_exceptionToActive?.includes(r),
      );
    const missingArchiveReasons = hasMissingArchiveReasons
      ? item.archive_reason.filter(
          (r) => !archiveReasons?.swm_exceptionToActive?.includes(r),
        )
      : [];

    const isParentEENotActive = checkIsParentEENotActive(
      selectedEEComponentDetails,
      item,
    );

    if (!hasMissingArchiveReasons && !isParentEENotActive) return null;

    const tooltipTitle = getSWMArchiveTooltipTitle(
      missingArchiveReasons,
      isParentEENotActive,
    );

    return (
      <CustomToolTip type="component" title={tooltipTitle}>
        <ErrorOutlineIcon sx={{ fontSize: "18px", color: "red" }} />
      </CustomToolTip>
    );
  };

  const handleActionTypeClick = (item) => {
    if (item.action_type === 1) {
      handleActionType1DialogOpen(item);
    } else if (item.action_type === 2) {
      handleActionType2DialogOpen(item);
    } else if (item.action_type === 4) {
      setSelectedSingleSWModule(item);
      setSelectedSWModuleUpdateDetails(item);
      setResumeSWMAttributeImpactFlowTrigger((prev) => prev + 1);
    } else if (item.action_type === 5) {
      setSelectedSingleSWModule(item);
      setSelectedSWModuleUpdateDetails(item);
      setOpenSWModuleUpdateDialog(true);
      setResumeSWMVersionImpactFlowTrigger((prev) => prev + 1);
    } else {
      handleActionType3DialogOpen(item);
    }
  };

  const [activeItemId, setActiveItemId] = useState(null); // State to track which item's actions are visible
  const [reason, setReason] = useState("");

  const [openSWModuleAddDialog, setOpenSWModuleAddDialog] = useState(false);
  const [copySWModuleDetails, setCopySWModuleDetails] = useState(null);
  const [openSWModuleUpdateDialog, setOpenSWModuleUpdateDialog] =
    useState(false);
  const [openSWModuleReplaceDialog, setOpenSWModuleReplaceDialog] =
    useState(false);
  const [openSWModuleChangeLogsDialog, setOpenSWModuleChangeLogsDialog] =
    useState(false);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);
  const [isSWMVersionImpactFlow, setIsSWMVersionImpactFlow] = useState(false);
  const [
    resumeSWMAttributeImpactFlowTrigger,
    setResumeSWMAttributeImpactFlowTrigger,
  ] = useState(0);
  const [
    resumeSWMVersionImpactFlowTrigger,
    setResumeSWMVersionImpactFlowTrigger,
  ] = useState(0);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);

  const [selectedSWModuleUpdateDetails, setSelectedSWModuleUpdateDetails] =
    useState(null);
  const [selectedSWModuleReplaceDetails, setSelectedSWModuleReplaceDetails] =
    useState(null);
  const [selectedSingleSWModule, setSelectedSingleSWModule] = useState([]);

  const [vfOptionsList, setVfOptionsList] = useState([]);
  const [supplierOptionsList, setSupplierOptionsList] = useState([]);

  const [swChangeLogsList, setSWChangeLogsList] = useState([]);
  const [swLogsDialogData, setSWLogsDialogData] = useState({});

  const [requestsDialogOpen, setRequestsDialogOpen] = useState(false);

  const [getRequestsData, setGetRequestsData] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const moreBtnRefs = useRef({});

  const [selectedSWM, setSelectedSWM] = useState(null);

  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);
  const [openDialogDraftToAdd, setOpenDialogDraftToAdd] = useState(false);
  const [reasonError, setReasonError] = useState("");
  const [confirmArchiveDialogOpen, setConfirmArchiveDialogOpen] =
    useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);

  const [gitSWMLogsDetails, setGitSWMLogsDetails] = useState([]);

  const [openLockUnLockSWMDialog, setOpenLockUnLockSWMDialog] = useState(null);
  const [lockUnLockSWMReason, setLockUnLockSWMReason] = useState(null);
  const [lockUnLockSWMData, setLockUnLockSWMData] = useState(null);
  const [openGitSWMDialog, setOpenGitSWMDialog] = useState(false);

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsItem, setDetailsItem] = useState(null);

  const [openEditDetailsDialog, setOpenEditDetailsDialog] = useState(false);
  const [selectedSwmItem, setSelectedSwmItem] = useState(null);
  const [totalSWMChangeLogCount, setTotalSWMChangeLogCount] = useState(0);
  const [swmDetailsDialogTabs, setSwmDetailsDialogTabs] = useState([
    "Details",
    "Log",
    "Attribute",
  ]);
  const [swmSelectedData, setSwmSelectedData] = useState(null);
  const [attributeDecisionMatrixContext, setAttributeDecisionMatrixContext] =
    useState(null);
  const [showAttributeDecisionMatrix, setShowAttributeDecisionMatrix] =
    useState(false);
  const [attributeExecutionId, setAttributeExecutionId] = useState(null);
  const [attributeSimilarTickets, setAttributeSimilarTickets] = useState([]);
  const [attributeSelectedEntity, setAttributeSelectedEntity] = useState(null);
  const [attributeSelectedTicket, setAttributeSelectedTicket] = useState(null);
  const [attributeOpenAiPanel, setAttributeOpenAiPanel] = useState(false);
  const [attributeCreateTicketData, setAttributeCreateTicketData] = useState(
    {},
  );
  const [attributeDecisionMatrixReason, setAttributeDecisionMatrixReason] =
    useState("");
  const [attributeDisabledStandards, setAttributeDisabledStandards] = useState(
    [],
  );
  useEffect(() => {
    dispatch(HandleApiActions({ ...adminSettingsAPI.get_admin_settings }));
  }, [dispatch]);

  useEffect(() => {
    if (adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.length > 0) {
      const supplierDetails =
        adminSettingsDetails?.adminSettings?.data?.ecu_suppliers?.map(
          (data) => ({
            value: data.name,
            label: data.name,
          }),
        );
      setSupplierOptionsList(supplierDetails);
    }
  }, [adminSettingsDetails]);

  const handleOpenGitHubLogs = async (item) => {
    handleCloseMenu();

    setOpenGitSWMDialog(true);

    const reqObj = {
      project_id: item?.project_id,
      variant_id: item?.variant_id,
      ee_id: item?.ee_id,
      swm_id: item?.swm_id,
    };

    try {
      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...githubIntegrationAction.get_git_swm_logs,
            params: reqObj,
          }),
        ),
      );
      setGitSWMLogsDetails(resp?.data);
    } catch (error) {
      console.error("Error fetching GitHub SWM Logs:", error);
    }
  };








  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    getSWModulesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    swmSelectedSearchField,
    swmSearchFieldValue,
    viewArchived,
  ]);

  const handleFilterChange = useCallback((searchField, searchValue) => {
    setSWMSelectedSearchField(searchField);
    setSWMSearchFieldValue(searchValue);
    setPage(1);
  }, [setSWMSelectedSearchField, setSWMSearchFieldValue, setPage]);

  const handleFilterResetAll = useCallback(() => {
    handleFilterChange("name");
  }, [handleFilterChange]);

  useEffect(() => {
    handleFilterResetAll();
  }, [selectedProjectDetails, selectedSingleVariantDetails, handleFilterResetAll]);

  const handlePageChange = (selectedPage) => {
    setPage(selectedPage);
  };

  const handleLockUnLockSWM = (params) => {
    setLockUnLockSWMData(params);

    setOpenLockUnLockSWMDialog(true);
  };
  const handleConfirmLockUnLockSWM = async () => {
    let apiDetails = labCar.lock_unlock_software_module;

    if (
      selectedProjectDetails?.project_id &&
      selectedSingleVariantDetails?.variant_id &&
      selectedEEComponentDetails?.ee_id
    ) {
      customConsole({
        ...apiDetails,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedSingleVariantDetails?.variant_id),
          ee_id: Number(selectedEEComponentDetails?.ee_id),
          swm_id: lockUnLockSWMData?.swmId,
          is_locked: lockUnLockSWMData?.isLocked,
          ...(lockUnLockSWMReason && { reason: lockUnLockSWMReason }),
        },
        show_toast: true,
      });

      try {
        await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...apiDetails,
              params: {
                project_id: Number(selectedProjectDetails?.project_id),
                variant_id: Number(selectedSingleVariantDetails?.variant_id),
                ee_id: Number(selectedEEComponentDetails?.ee_id),
                swm_id: lockUnLockSWMData?.swmId,
                is_locked: lockUnLockSWMData?.isLocked,
                ...(lockUnLockSWMReason && { reason: lockUnLockSWMReason }),
              },
              show_toast: true,
            }),
          ),
        );
        await getSWModulesList({ force: true });
        handleCloseLockUnLockSWM();
      } catch (error) {
        console.error("Failed to update lock status", error);
      }
    }
  };
  const handleCloseLockUnLockSWM = () => {
    setOpenLockUnLockSWMDialog(false);

    setTimeout(() => {
      setLockUnLockSWMData(null);
      setLockUnLockSWMReason("");
    }, 300);
  };

  const handleSelectedSWModuleUpdateDetails = (sWModule) => {
    setOpenSWModuleUpdateDialog(true);

    if (sWModule) {
      setSelectedSWModuleUpdateDetails(sWModule);
    }
  };

  const handleSelectedSWModuleReplaceDetails = (sWModule) => {
    setOpenSWModuleReplaceDialog(true);

    if (sWModule) {
      setSelectedSWModuleReplaceDetails(sWModule);
    }
  };

  const handleGetSWModuleChangeLogs = async ({ swm_id, pageNo, pageSize }) => {
    let apiDetails = labCar.get_sw_module_logs;

    if (
      selectedProjectDetails?.project_id &&
      selectedEEComponentDetails?.ee_id
    ) {
      customConsole({
        project_id: Number(selectedProjectDetails?.project_id),
        ee_id: Number(selectedEEComponentDetails?.ee_id),
        swm_id,
        page_number: pageNo,
        page_size: pageSize,
      });

      const resp = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...apiDetails,
            params: {
              project_id: Number(selectedProjectDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedEEComponentDetails?.ee_id),
              swm_id,
              page_number: pageNo,
              page_size: pageSize,
            },
            show_toast: false,
          }),
        ),
      );
      if (resp.error) {
        setSWChangeLogsList([]);
      } else {
        setSWChangeLogsList(resp?.data);
        setTotalSWMChangeLogCount(resp?.total_count);
      }
    }
  };

  const handleSWLogsDialogOpen = (data) => {
    setOpenSWModuleChangeLogsDialog(true);


    setSWLogsDialogData(data);
  };

  const handleSWViewDialogOpen = (data) => {
    setSelectedSwmItem(data);
    setSWLogsDialogData(data);
    setSwmDetailsDialogTabs(["Details", "Attribute"]);
    setOpenSWModuleChangeLogsDialog(true);
  };

  const handleSWLogsDialogClose = () => {
    setSWLogsDialogData(null);
    setSWChangeLogsList([]);
    setSwmDetailsDialogTabs(["Details", "Log", "Attribute"]);

    setOpenSWModuleChangeLogsDialog(false);
  };

  const handleSWModuleCopyOpen = () => {
    const swModuleToCopy = selectedSwmItem || swLogsDialogData || selectedSWM;

    if (!swModuleToCopy) return;

    setCopySWModuleDetails(swModuleToCopy);
    setOpenSWModuleChangeLogsDialog(false);
    setOpenSWModuleAddDialog(true);
  };

  useEffect(() => {

    if (selectedProjectDetails?.vehicle_function_details) {
      setVfOptionsList(selectedProjectDetails?.vehicle_function_details || []);
    }
  }, [selectedProjectDetails]);

  useEffect(() => {
    customConsole(
      "selectedSWModuleUpdateDetails: " + selectedSWModuleUpdateDetails,
    );
    customConsole(
      "selectedSWModuleReplaceDetails: " + selectedSWModuleReplaceDetails,
    );
  }, [selectedSWModuleUpdateDetails, selectedSWModuleReplaceDetails]);

  const handleArchivedSWMCheckboxChange = (swmId, checked) => {
    if (checked) {
      setSelectedArchivedSWMs((prev) => [...(prev || []), swmId]);
    } else {
      setSelectedArchivedSWMs((prev) => (prev || []).filter((id) => id !== swmId));
    }
  };

  const handleDialogClose = () => {
    setReason(""); // Reset reason
  };

  const handleSubmit = () => {
    const reqObj = {
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      swm_id: Number(selectedSWM?.swm_id),
      request_reason: reason,
    };

    dispatch(
      HandleApiActions({
        ...editRequestAPI.create_edit_requests,
        params: reqObj,
        show_toast: true,
      }),
    );
    setReason("");
    handleDialogClose();
  };

  const handleActionType3DialogOpen = (swm) => {
    setSelectedSWM(swm);
    setPendingDialogOpen(true);
  };

  const handlePendingDialogClose = () => {
    setPendingDialogOpen(false);
  };

  const pendingActionRequired = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      swm_id: Number(selectedSWM?.swm_id),
      is_dependency_linking: true,
      module_type: "MODULE LINKING",
    };
    try {
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: reqObj,
            show_toast: false,
          }),
        ),
      );
      await getSWModulesList({ force: true });
      handlePendingDialogClose();
    } catch (error) {
      console.error("Failed to update action type", error);
    }
  };

  const handleMoreClick = (e, item) => {
    if (e?.stopPropagation) e.stopPropagation();
    const anchor =
      e?.currentTarget || moreBtnRefs.current?.[item?.swm_id] || null;
    setAnchorEl(anchor);
    setActiveItemId(item.swm_id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };

  const handleRequestsDialogOpen = (item) => {
    setSwmSelectedData(item);
    setSelectedSWM(item);
    setRequestsDialogOpen(true);
    void getRequests(item);
  };

  const handleRequestsDialogClose = () => {
    setRequestsDialogOpen(false);
  };

  const handleMenuAction = (action, item) => {
    handleCloseMenu(); // Close the menu after action
    if (action === "update") {
      handleSelectedSWModuleUpdateDetails(item);
    } else if (action === "replace") {
      handleSelectedSWModuleReplaceDetails(item);
    } else if (action === "logs") {
      setSwmDetailsDialogTabs(["Log"]);
      setSelectedSwmItem(item);
      handleSWLogsDialogOpen(item);
    } else if (action === "requests") {
      handleRequestsDialogOpen(item);
    } else if (action === "view") {
      handleSWViewDialogOpen(item);
    } else if (action === "details") {
      setDetailsItem(item); // <-- This updates detailsItem
      setOpenDetailsDialog(true); // <-- This opens the dialog
    } else if (action === "updateAttribute") {
      setSelectedSingleSWModule(item);
      setSelectedSWModuleUpdateDetails(item);
      setOpenAttributeUpdateDialog(true);
    } else if (action === "archive") {
      setItemToArchive(item);
      setConfirmArchiveDialogOpen(true);
    }
  };

  const handleConfirmArchiveSWM = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const item = itemToArchive;
    if (!projectId || !variantId || !item?.swm_id) {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
      return;
    }

    try {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.archive_software_module,
            params: {
              swm_ids: [item.swm_id],
              project_id: Number(projectId),
              variant_id: Number(variantId),
            },
            show_toast: true,
          }),
        ),
      );
      if (!res?.error) {
        if (typeof getSWModulesList === "function") {
          getSWModulesList({ force: true });
        }
      }
    } catch (err) {
      console.error("Error archiving software module:", err);
    } finally {
      setConfirmArchiveDialogOpen(false);
      setItemToArchive(null);
    }
  };

  const loadAttributeDecisionMatrixContext = async () => {
    const targetSWM = selectedSWModuleUpdateDetails || selectedSingleSWModule;

    if (
      !targetSWM?.swm_id ||
      !selectedProjectDetails?.project_id ||
      !selectedSingleVariantDetails?.variant_id
    ) {
      return;
    }

    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "SWM",
            component_id: targetSWM.swm_id,
            project_id: selectedProjectDetails.project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
          },
          show_toast: false,
        }),
      ),
    );

    const contextData = standardsRes?.data;

    if (!isDecisionMatrixContextValid(contextData)) {
      setAttributeDecisionMatrixContext(null);
      setShowAttributeDecisionMatrix(false);
      return;
    }

    setAttributeDecisionMatrixContext(contextData);
    setAttributeDecisionMatrixReason(reason?.trim() || "");
    setShowAttributeDecisionMatrix(true);
  };

  const handleAttributeDecisionMatrixClose = () => {
    setShowAttributeDecisionMatrix(false);
    setAttributeDecisionMatrixContext(null);
    setAttributeExecutionId(null);
    setAttributeSimilarTickets([]);
    setAttributeSelectedEntity(null);
    setAttributeSelectedTicket(null);
    setAttributeOpenAiPanel(false);
    setAttributeCreateTicketData({});
    setAttributeDecisionMatrixReason("");
    setAttributeDisabledStandards([]);
  };

  const handleAttributeExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload.workflowId,
              version: payload.version,
            },
            show_toast: false,
          }),
        ),
      );

      setAttributeExecutionId(res.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      return dispatch(
        HandleApiActions({
          ...ruleEngineActions.wfe_execution_node_complete,
          params: {
            executionId: payload.executionId,
            nodeId: payload.nodeId,
            input: payload.input,
          },
          show_toast: false,
        }),
      );
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleAttributeViewExecution = (executionId) => {
  };

  const handleAttributeDecisionMatrixAction = async ({ standardCode } = {}) => {
    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.re_create_ticket_v1,
          params: {
            details: attributeCreateTicketData,
            ...(attributeDecisionMatrixReason && {
              reason: attributeDecisionMatrixReason,
            }),
          },
          show_toast: true,
        }),
      ),
    );

    if (!res?.error && standardCode) {
      setAttributeDisabledStandards((prev) => [
        ...new Set([...prev, standardCode]),
      ]);
    }

    return res;
  };

  const handelGetAttributeSimilerTickets = async ({ standard_name } = {}) => {
    const targetSWM = selectedSWModuleUpdateDetails || selectedSingleSWModule;

    const res = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.get_similar_tickets,
          params: {
            execution_id: attributeExecutionId,
            entity_type: "swm",
            entity_id: Number(targetSWM?.swm_id),
            ...(standard_name ? { standard_name } : {}),
          },
          show_toast: true,
        }),
      ),
    );

    setAttributeSimilarTickets(res?.data || []);
    setAttributeCreateTicketData(res?.data || []);
    return res;
  };

  const handelGetAttributeAiSuggestion = (ticket) => {
    setAttributeSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "Software Module",
    });
    setAttributeOpenAiPanel(true);
    setAttributeSelectedTicket(ticket);
  };

  const handlePendingDialogSuccess = () => {
    handlePendingDialogClose();
    const targetItem = selectedSWM || swmSelectedData;
    if (targetItem) {
      const anchor =
        moreBtnRefs.current?.[targetItem?.swm_id] ||
        (typeof document === "undefined"
          ? null
          : document.querySelector(`[data-swm-id="${targetItem?.swm_id}"]`));
      if (anchor) {
        setAnchorEl(anchor);
        setActiveItemId(targetItem.swm_id);
      }
    }
  };
  const getRequests = async (item) => {
    let apiDetails = editRequestAPI?.get_edit_requests;
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            swm_id: item?.swm_id,
            is_sent_requests: true,
            page_number: 1,
            page_size: 25,
          },
          show_toast: false,
        }),
      ),
    );
    if (resp.error) {
      setGetRequestsData([]);
    } else {
      setGetRequestsData(resp.data);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 100:
        return "Pending";
      case 200:
        return "Approved";
      case 300:
        return "Rejected";
      default:
        return "Unknown Status";
    }
  };

  const handleActionType1DialogOpen = (swm) => {
    setSelectedSWM(swm);
    setActionType1DialogOpen(true);
  };

  const handleActionType2DialogOpen = (swm) => {
    setSelectedSWM(swm);
    setActionType2DialogOpen(true);
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };


  const getParentSWMDetails = async () => {

    let parentSWMResponse = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar.get_parent_swm,
          params: {
            project_id: selectedProjectDetails.project_id,
            parent_project_id: selectedProjectDetails.parent_project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
            parent_variant_id:
              selectedSingleVariantDetails?.parent_variant_details?.variant_id,
            ee_id: selectedEEComponentDetails.ee_id,
            parent_ee_id: selectedEEComponentDetails?.parent_ee_id,
          },
        }),
      ),
    );
    if (parentSWMResponse.error) {
      showToastMessage({
        type: "error",
        message: parentSWMResponse.msg,
      });
      setRecreationParentSWMList([]);
      setSelectedParentSWMDetails(null);
    } else {
      setSelectedParentSWMDetails(parentSWMResponse.data[0]);
      setRecreationParentSWMList(parentSWMResponse.data);
      setOpenRecreationSWMDialog(true);
    }
  };

  const handleSWMRecreateOpen = async () => {
    await getParentSWMDetails();
  };

  const [swmNameSearch, setSwmNameSearch] = useState("");
  const [openRecreationSWMDialog, setOpenRecreationSWMDialog] = useState(false);
  const [selectedParentSWMDetails, setSelectedParentSWMDetails] =
    useState(null);
  const [recreationParentSWMList, setRecreationParentSWMList] = useState([]);

  const debounceRef = useRef();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSWMSelectedSearchField("name"); // Always search by name
      setSWMSearchFieldValue(swmNameSearch);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [swmNameSearch, setSWMSelectedSearchField, setSWMSearchFieldValue]);

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...typeApprovalAPI.get_tickets,
            params: reqObj,
            show_toast: false,
          }),
        ),
      );

      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              ?.map((ticket) => ticket?.ls_details?.standard_number)
              ?.filter(Boolean)
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        const standardWithRuleSet = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...ruleEngineActions.get_rule_engine_with_standards,
              params: { standard: standardsWithRuleSet },
              show_toast: false,
            }),
          ),
        );
        return standardWithRuleSet?.data || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch standard with ruleset", error);
      return false;
    }
  };

  const formatHeading = (text, max = 30) => {
    const fallback = "E/E Not Available";
    if (!text) return fallback;
    const str = String(text);
    return str?.length > max ? str.slice(0, max - 3) + "..." : str;
  };
  const vlcHeaderTitle = formatHeading(heading || "E/E Not Available", 30);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%" }}>
        <Card
          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "450px",
          }}
        >
          <CardHeader
            sx={vlcComponentStyle.cardHeader}
            title={
              <VlcCardHeader
                title={vlcHeaderTitle}
                showRecreateIcon={
                  Boolean(
                    selectedProjectDetails?.parent_project_id &&
                    selectedSingleVariantDetails?.parent_variant_details
                      ?.variant_id &&
                    selectedEEComponentDetails?.parent_ee_id,
                  )
                }
                onRecreateClick={handleSWMRecreateOpen}
                selectedEEComponentDetails={selectedEEComponentDetails}
                variantOptionList={variantOptionList}
                selectedSingleVariantDetails={selectedSingleVariantDetails}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  sx={{
                    width: "100%",
                    mt: 1,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search Number"
                    variant="outlined"
                    fullWidth
                    sx={componentStyle.searchField.textField}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    value={swmNameSearch}
                    onChange={(e) => setSwmNameSearch(e.target.value)}
                  />
                  <Box>
                    {!supplier && !viewArchived && (
                      <AddButtonV2
                        disabled={
                          !selectedEEComponentDetails?.ee_id ||
                          selectedEEComponentDetails?.status === false
                        }
                        handleOnClick={() => setOpenSWModuleAddDialog(true)}
                      />
                    )}
                  </Box>
                </Box>
              </VlcCardHeader>
            }
          />
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 0,
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            {sWModulesList?.length > 0 && heading ? (
              sWModulesList?.map((item) => (
                <VlcCardContent
                  onClick={() => {}}
                  data={item}
                  key={item.swm_id}
                  isSelected={false}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "stretch",
                    }}
                  >
                    {item?.archive_status?.toUpperCase() === "ARCHIVED" && (
                        <Box 
                          sx={{ 
                            p: 1, 
                            pb: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }} 
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CustomCheckBoxV2
                            noPaddingRequired={true}
                            key={`swm-${item.swm_id}-${clearAllCounter}`}
                            value={selectedArchivedSWMs?.includes(item.swm_id)}
                            disabled={
                              selectedArchivedEEs?.length > 0 ||
                              (Array.isArray(item?.archive_reason)
                                ? item.archive_reason.some(
                                    (r) =>
                                      !archiveReasons?.swm_exceptionToActive?.includes(
                                        r,
                                      ),
                                  )
                                : Boolean(item?.archive_reason) &&
                                  !archiveReasons?.swm_exceptionToActive?.includes(
                                    item.archive_reason,
                                  ))
                            }
                            onChange={(e) =>
                              handleArchivedSWMCheckboxChange(
                                item.swm_id,
                                e.target.checked,
                              )
                            }
                          />
                        </Box>
                      )}
                    <Box
                      sx={{
                        width: "70%",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start", // ✅ ensures left alignment
                          p: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                        >
                          <CustomToolTip
                            type="string"
                            length={60}
                            style={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              fontWeight: "bold",
                            }}
                            title={`Number: ${item?.name ?? ""}`}
                          >
                            {`Number: ${item?.name ?? ""}`}
                          </CustomToolTip>
                        </Typography>

                        <Typography
                          sx={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            color: vlcCardStyles.vlcCardListColor,
                          }}
                        >
                          <CustomToolTip
                            type="string"
                            length={60}
                            style={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                            title={`Version: ${item?.version ?? ""}`}
                          >
                            {`Version: ${item?.version ?? ""}`}
                          </CustomToolTip>
                        </Typography>

                        <Typography
                          sx={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            color: vlcCardStyles.vlcCardListColor,
                          }}
                        >
                          <CustomToolTip
                            type="string"
                            length={60}
                            style={{
                              ...componentStyle.font.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                            title={`Description: ${item?.description ?? ""}`}
                          >
                            {`Description: ${item?.description ?? ""}`}
                          </CustomToolTip>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          ml: 0.5,
                          mt: -0.5,
                        }}
                      >
                        {item?.status === false ? (
                          <Box sx={{ pl: 0.5, pb: 0.5 }}>
                            <Chip
                              onClick={() => {
                                setOpenDialogDraftToAdd(true);
                                setSelectedSingleSWModule(item);
                              }}
                              sx={{
                                borderRadius: 0,
                                color: "#007CB0",
                                borderColor: "#007CB0",
                                borderWidth: 1,
                                fontSize: "11px",
                              }}
                              size="small"
                              variant="outlined"
                              label={"Draft"}
                            />
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        key={item?.swm_id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                              flexWrap: "nowrap",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            {renderSWMArchiveErrorIcon(item)}
                            {item.action_type !== 0 && item.status === true && (
                              <CustomToolTip
                                type="component"
                                title={getActionTooltipTitle(item.action_type)}
                              >
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={() => handleActionTypeClick(item)}
                                >
                                  <ErrorOutlineIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </CustomToolTip>
                            )}
                            {item?.is_locked ? (
                              <CustomToolTip type="component" title="Lock">
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLockUnLockSWM({
                                      swmId: item?.swm_id,
                                      isLocked: false,
                                    });
                                  }}
                                >
                                  <LockIcon sx={{ fontSize: "18px" }} />
                                </IconButton>
                              </CustomToolTip>
                            ) : (
                              <CustomToolTip type="component" title="UnLock">
                                <IconButton
                                  size="small"
                                  sx={{ color: "black" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLockUnLockSWM({
                                      swmId: item?.swm_id,
                                      isLocked: true,
                                    });
                                  }}
                                >
                                  <NoEncryptionGmailerrorredIcon
                                    sx={{ fontSize: "18px" }}
                                  />
                                </IconButton>
                              </CustomToolTip>
                            )}
                            <CustomToolTip type="component" title="More">
                              <IconButton
                                size="small"
                                sx={{ color: "black" }}
                                data-swm-id={item?.swm_id}
                                ref={(el) => {
                                  if (el)
                                    moreBtnRefs.current[item?.swm_id] = el;
                                }}
                                onClick={(e) => handleMoreClick(e, item)}
                              >
                                <MoreVertIcon sx={{ fontSize: "18px" }} />
                              </IconButton>
                            </CustomToolTip>
                          </Box>
                        </Box>

                        <Menu
                          anchorEl={anchorEl}
                          open={
                            Boolean(anchorEl) && activeItemId === item?.swm_id
                          }
                          onClose={handleCloseMenu}
                          slotProps={{
                            paper: {
                              sx: {
                                borderRadius: 0, // ✅ remove rounding completely
                                boxShadow: 1, // optional: remove shadow if you want a flat look
                              },
                            },
                          }}
                        >
                          {!item?.is_locked && canEditItem(item) ? (
                            <>
                              <MenuItem
                                onClick={() => handleMenuAction("view", item)}
                                sx={{ ...componentStyle.font.cardText }}
                              >
                                View
                              </MenuItem>

                              {item?.action_type === 0 ||
                              item?.action_type === 3 ? (
                                <MenuItem
                                  onClick={() => {
                                    if (viewArchived) {
                                      handleCloseMenu();
                                      handleSelectedSWModuleReplaceDetails(item);
                                    } else {
                                      setOpenEditDetailsDialog(true);
                                      setSelectedSwmItem(item);
                                    }
                                  }}
                                  sx={{ ...componentStyle.font.cardText }}
                                >
                                  Edit Details
                                </MenuItem>
                              ) : null}
                            </>
                          ) : (
                            <MenuItem
                              onClick={() => handleMenuAction("view", item)}
                              sx={{ ...componentStyle.font.cardText }}
                            >
                              View
                            </MenuItem>
                          )}
                          {!canEditItem(item) && (
                            <MenuItem
                              onClick={() => handleMenuAction("requests", item)}
                              sx={{ ...componentStyle.font.cardText }}
                            >
                              Edit Request
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={() => handleMenuAction("logs", item)}
                            sx={{ ...componentStyle.font.cardText }}
                          >
                            Log
                          </MenuItem>
                          {item?.archive_status?.toUpperCase() !== "ARCHIVED" &&
                            projectDetails?.status === PROJECT_STATUS.DRAFT &&
                            Boolean(projectDetails?.archive_vlc) && (
                            <MenuItem
                              onClick={() => handleMenuAction("archive", item)}
                              sx={{ ...componentStyle.font.cardText }}
                            >
                              Archive
                            </MenuItem>
                          )}
                          {item?.meta_details && (
                            <MenuItem
                              onClick={() => handleOpenGitHubLogs(item)}
                              sx={{ ...componentStyle.font.cardText }}
                            >
                              GitHub
                            </MenuItem>
                          )}
                        </Menu>
                      </Box>
                    </Box>
                  </Box>
                </VlcCardContent>
              ))
            ) : (
              <Typography variant="body1" sx={styles.noDataAvailableTxt}>
                No data available
              </Typography>
            )}
          </CardContent>
          <Paper
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: "40px",
              borderTop: "1px solid #ddd",
              borderRadius: "0px !important",
            }}
          >
            {totalSWMCount > 0 && (
              <CustomPaginationV2
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                totalItems={totalSWMCount}
              />
            )}
          </Paper>
        </Card>
      </Box>

      <CustomDialogBoxV2
        open={openLockUnLockSWMDialog}
        onClose={handleCloseLockUnLockSWM}
        title={`Reason to ${lockUnLockSWMData?.isLocked ? "Lock" : "UnLock"}`}
        successButtonName={"Confirm"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleConfirmLockUnLockSWM}
        onClickReject={handleCloseLockUnLockSWM}
        actionButtonStyle={"continuous"}
        onDisabled={lockUnLockSWMReason?.length < 3}
      >
        <Stack spacing={2} sx={{ width: "350px" }}>
          <CustomFormTextFieldV2
            label="Reason"
            value={lockUnLockSWMReason}
            setValue={setLockUnLockSWMReason}
            required={true}
          />
        </Stack>
      </CustomDialogBoxV2>


      <EditDetailsDialog
        openEditDetails={openEditDetailsDialog}
        setOpenEditDetails={setOpenEditDetailsDialog}
        onClick={(type) => {
          setOpenEditDetailsDialog(false);
          handleMenuAction(type, selectedSwmItem);
        }}
      />

      <GithubSWMLogs
        gitSWMLogsDetails={gitSWMLogsDetails}
        setGitSWMLogsDetails={setGitSWMLogsDetails}
        openGitSWMDialog={openGitSWMDialog}
        setOpenGitSWMDialog={setOpenGitSWMDialog}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        handleSWModuleUpdate={handleSWModuleUpdate}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        selectedSWM={selectedSWM}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        checkForStandardWithRuleSet={checkForStandardWithRuleSet}
        sWModulesList={sWModulesList}
      />
      <VlcSWModuleAddDialog
        supplierOptionsList={supplierOptionsList}
        selectedVariantsMany={selectedVariantsMany}
        getSWModulesList={getSWModulesList}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleAddDialog={openSWModuleAddDialog}
        setOpenSWModuleAddDialog={setOpenSWModuleAddDialog}
        handleSWModuleAdd={handleSWModuleAdd}
        vfOptionsList={vfOptionsList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        copySWModuleDetails={copySWModuleDetails}
        onCopyPrefillClear={() => setCopySWModuleDetails(null)}
        viewArchived={viewArchived}
      />
      <VlcSWModuleUpdateDialog
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleUpdateDialog={openSWModuleUpdateDialog}
        setOpenSWModuleUpdateDialog={setOpenSWModuleUpdateDialog}
        handleSWModuleUpdate={handleSWModuleUpdate}
        selectedSWModuleUpdateDetails={selectedSWModuleUpdateDetails}
        setSelectedSWModuleUpdateDetails={setSelectedSWModuleUpdateDetails}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        selectedSWM={selectedSWM}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        isSWMVersionImpactFlow={isSWMVersionImpactFlow}
        setIsSWMVersionImpactFlow={setIsSWMVersionImpactFlow}
        resumeSWMAttributeImpactFlowTrigger={
          resumeSWMAttributeImpactFlowTrigger
        }
        resumeSWMVersionImpactFlowTrigger={resumeSWMVersionImpactFlowTrigger}
        onRedirectedActionDialogClose={onRedirectedActionDialogClose}
        viewArchived={viewArchived}
      />
      <VlcSWModuleReplaceDialog
        supplierOptionsList={supplierOptionsList}
        title={"Add Draft Software Module"}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleReplaceDialog={openDialogDraftToAdd}
        setOpenSWModuleReplaceDialog={setOpenDialogDraftToAdd}
        handleSWModuleReplace={handleSWModuleReplace}
        selectedSWModuleReplaceDetails={selectedSingleSWModule}
        setSelectedSWModuleReplaceDetails={setSelectedSWModuleReplaceDetails}
        vfOptionsList={vfOptionsList}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        viewArchived={viewArchived}
      />
      <VlcSWModuleReplaceDialog
        supplierOptionsList={supplierOptionsList}
        title={viewArchived ? "Draft Software Module" : "Replace Software Module"}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleReplaceDialog={openSWModuleReplaceDialog}
        setOpenSWModuleReplaceDialog={setOpenSWModuleReplaceDialog}
        handleSWModuleReplace={handleSWModuleReplace}
        selectedSWModuleReplaceDetails={selectedSWModuleReplaceDetails}
        setSelectedSWModuleReplaceDetails={setSelectedSWModuleReplaceDetails}
        vfOptionsList={vfOptionsList}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        viewArchived={viewArchived}
      />

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
          setIsSWMVersionImpactFlow(false);
          onRedirectedActionDialogClose?.();
        }}
        onClickSuccess={() => {
          getSWModulesList({ force: true });
          setOpenAttributeUpdateDialog(false);
        }}
        onShowUpdateDialog={() => {
          setIsSWMVersionImpactFlow(true);
          setSelectedSWModuleUpdateDetails(selectedSingleSWModule);
          getSWModulesList({ force: true });
          setTimeout(() => {
            setOpenSWModuleUpdateDialog(true);
          }, 300);
        }}
        selectedComponent={selectedSingleSWModule}
        componentType={"swm"}
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadAttributeDecisionMatrixContext}
      />

      <Dialog
        open={showAttributeDecisionMatrix}
        onClose={handleAttributeDecisionMatrixClose}
        fullScreen
        slotProps={{
          paper: {
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          },
        }}
      >
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: "#26890D",
          }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Decision Matrix
            </Typography>
            <IconButton
              color="inherit"
              onClick={handleAttributeDecisionMatrixClose}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flex: 1,
            height: "calc(100vh - 64px)",
            overflow: "auto",
            p: 2,
          }}
        >
          {showAttributeDecisionMatrix && (
            <DecisionMatrixPanel
              context={attributeDecisionMatrixContext}
              key={showAttributeDecisionMatrix ? "open" : "closed"}
              onExecuteWorkflow={handleAttributeExecuteWorkflow}
              onViewExecution={handleAttributeViewExecution}
              onClose={handleAttributeDecisionMatrixClose}
              onDecisionMatrixAction={handleAttributeDecisionMatrixAction}
              handelGetSimilerTickets={handelGetAttributeSimilerTickets}
              similarTickets={attributeSimilarTickets}
              setSimilarTickets={setAttributeSimilarTickets}
              entityDetails={attributeSelectedEntity}
              handelGetAiSuggestion={handelGetAttributeAiSuggestion}
              selectedTicket={attributeSelectedTicket}
              setSelectedTicket={setAttributeSelectedTicket}
              openAiPanel={attributeOpenAiPanel}
              setOpenAiPanel={setAttributeOpenAiPanel}
              setCreateTicketData={setAttributeCreateTicketData}
              disabledStandards={attributeDisabledStandards}
            />
          )}
        </Box>
      </Dialog>

      <RecreateSWMDialog
        supplierOptionsList={supplierOptionsList}
        title={"Recreate Software Module"}
        selectedVariantsMany={selectedVariantsMany}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedEEComponentDetails={selectedEEComponentDetails}
        openSWModuleReplaceDialog={openRecreationSWMDialog}
        setOpenSWModuleReplaceDialog={setOpenRecreationSWMDialog}
        handleSWModuleReplace={handleSWModuleReplace}
        selectedSWModuleReplaceDetails={selectedParentSWMDetails}
        setSelectedSWModuleReplaceDetails={setSelectedSWModuleReplaceDetails}
        vfOptionsList={vfOptionsList}
        setIsLinkSoftwareModulesScreen={setIsLinkSoftwareModulesScreen}
        selectedZone={selectedZone}
        getSWModulesList={getSWModulesList}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        recreationParentSWMList={recreationParentSWMList}
        setRecreationParentSWMList={setRecreationParentSWMList}
        selectedParentSWMDetails={selectedParentSWMDetails}
        setSelectedParentSWMDetails={setSelectedParentSWMDetails}
      />

      <CustomDialogBoxV2
        open={openSWModuleChangeLogsDialog}
        onClose={handleSWLogsDialogClose}
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap:2
            }}
          >
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Software Modules
            </Typography>
            <IconButton
              size="small"
              onClick={handleSWModuleCopyOpen}
              disabled={!selectedSwmItem && !swLogsDialogData && !selectedSWM}
              aria-label="Copy Software Module"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          <DetailsLogTab
            logData={swLogsDialogData}
            ChangeLogsList={swChangeLogsList}
            getLogsData={handleGetSWModuleChangeLogs}
            selectedComponent={selectedSwmItem}
            zone_name={selectedEEComponentDetails?.zone_details?.zone_name}
            title={"Update Draft SWM Module"}
            totalLogsCount={totalSWMChangeLogCount}
            supplierOptionsList={supplierOptionsList}
            variantOptionList={variantOptionList}
            adminSettingsDetails={adminSettingsDetails}
            openEditDetailsDialog={openSWModuleChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenSWModuleChangeLogsDialog}
            getSWModulesList={getSWModulesList}
            selectedProjectDetails={selectedProjectDetails}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            selectedVariantsMany={selectedVariantsMany}
            handleSWModuleReplace={handleSWModuleReplace}
            selectedSWModuleReplaceDetails={selectedSwmItem}
            setSelectedSWModuleReplaceDetails={setSelectedSwmItem}
            vfOptionsList={vfOptionsList}
            type={"SWM"}
            visibleTabs={swmDetailsDialogTabs}
          />
        </Box>
      </CustomDialogBoxV2>
      <Box>
        <CustomDialogBoxV2
          open={requestsDialogOpen}
          onClose={handleRequestsDialogClose}
          title="Edit Requests"
        >
          <Box sx={{ width: "450px", height: "300px" }}>
            <RequestsAndLogsTabs
              getRequestsData={getRequestsData}
              getStatusLabel={getStatusLabel}
              reason={reason}
              setReason={setReason}
              reasonError={reasonError}
              setReasonError={setReasonError}
              handleSubmit={handleSubmit}
              onClose={handleRequestsDialogClose}
              selectedProjectDetails={selectedProjectDetails}
              data={swmSelectedData}
            />
          </Box>
        </CustomDialogBoxV2>


        <CustomDialogBoxV2
          open={pendingDialogOpen}
          onClose={handlePendingDialogClose}
          title={"Action Required"}
          onClickSuccess={handlePendingDialogSuccess} // Close and open More menu
          onClickReject={pendingActionRequired}
          actionButtonStyle={"continuous"}
          successButtonName={"Yes"}
          rejectButtonName={"No"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              {" "}
              EE Component has been updated/replaced. Will it affect this
              module?
            </Typography>
          </Stack>
        </CustomDialogBoxV2>


        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${selectedSWM?.swm_id}&ee_id=${
                selectedEEComponentDetails?.ee_id
              }&selectedZone=${
                selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${
                selectedEEComponentDetails?.ee_id
              }&zone=${selectedZone?.zone_position_name}&swm_id=${
                selectedSWM?.swm_id
              }&tab=${3}`,
            );
            setActionType2DialogOpen(false);
          }} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
            Software module Details
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: "60vh", // or "calc(100vh - 200px)" for more control
              overflowY: "auto",
              backgroundColor: "#fafafa",
              scrollbarColor: "#888 #f1f1f1",
              wordBreak: "break-word",
            }}
          >
            {detailsItem ? (
              <>
                <DetailRow label="Name" value={detailsItem.name} />
                <DetailRow
                  label="Description"
                  value={detailsItem.description}
                />
                <DetailRow label="Summary" value={detailsItem.summary || "-"} />
                <DetailRow label="Version" value={detailsItem.version} />

                <DetailRow
                  label="Supplier Name"
                  value={detailsItem.supplier_name || "-"}
                />

                <DetailRow
                  label="Vehicle Functions"
                  value={(detailsItem.vehicle_functions || [])
                    ?.map((v) => v.name || v)
                    ?.join(", ")}
                />

                <DetailRow
                  label="Created At"
                  value={new Date(detailsItem.created_at).toLocaleString()}
                />
                <DetailRow
                  label="Updated At"
                  value={new Date(detailsItem.updated_at).toLocaleString()}
                />
              </>
            ) : (
              <Typography>No details available.</Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
            <Button
              onClick={() => setOpenDetailsDialog(false)}
              sx={{ ...appTheme.commonBtnStyle }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {confirmArchiveDialogOpen && (
          <CustomDialogBoxV2
            open={confirmArchiveDialogOpen}
            onClose={() => {
              setConfirmArchiveDialogOpen(false);
              setItemToArchive(null);
            }}
            title="Confirm Changes"
            // dialogRadius="0rem"
            successButtonName="Confirm"
            rejectButtonName="Cancel"
            actionButtonStyle="continuous"
            onClickSuccess={handleConfirmArchiveSWM}
            onClickReject={() => {
              setConfirmArchiveDialogOpen(false);
              setItemToArchive(null);
            }}
          >
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "left", textAlign: "left", gap: 1 }}>
              <Typography>
               This change will affect the dependencies and intersections, if any are linked to the element.
              </Typography>
              <Typography>
                Are you sure you want to proceed?
              </Typography>
            </Box>
          </CustomDialogBoxV2>
        )}
      </Box>
    </Box>
  );
}

VlcSWModules.propTypes = {
  selectedVariantsMany: PropTypes.array,
  variantOptionList: PropTypes.array,
  heading: PropTypes.string,
  sWModulesList: PropTypes.array,
  handleSWModuleAdd: PropTypes.func,
  handleSWModuleUpdate: PropTypes.func,
  handleSWModuleReplace: PropTypes.func,
  setIsEEComponentSelected: PropTypes.func,
  setSelectedEEComponentDetails: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_project_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    vehicle_function_details: PropTypes.array,
    is_user_project_owner: PropTypes.bool,
    is_zonal: PropTypes.bool,
    project_zones: PropTypes.arrayOf(PropTypes.any),
  }),
  selectedEEComponentDetails: PropTypes.shape({
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variant_ids: PropTypes.array,
    zone_details: PropTypes.shape({
      zone_name: PropTypes.string,
      zone_position_name: PropTypes.string,
    }),
    archive_status: PropTypes.string,
    archive_reason: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    is_active: PropTypes.bool,
  }),
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  selectedZone: PropTypes.shape({
    zone_position_name: PropTypes.string,
  }),
  getSWModulesList: PropTypes.func,
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vehicle_function_details: PropTypes.array,
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  swmSearchFieldValue: PropTypes.string,
  setSWMSearchFieldValue: PropTypes.func,
  swmSelectedSearchField: PropTypes.string,
  setSWMSelectedSearchField: PropTypes.func,
  page: PropTypes.number,
  setPage: PropTypes.func,
  rowsPerPage: PropTypes.number,
  totalSWMCount: PropTypes.number,
  onRedirectedActionDialogClose: PropTypes.func,
  viewArchived: PropTypes.bool,
  selectedArchivedSWMs: PropTypes.arrayOf(PropTypes.any),
  setSelectedArchivedSWMs: PropTypes.func,
  selectedArchivedEEs: PropTypes.arrayOf(PropTypes.any),
  clearAllCounter: PropTypes.number,
};

export default React.memo(VlcSWModules);

function VlcSWModuleAddDialog({
  supplierOptionsList,
  getSWModulesList,
  selectedProjectDetails,
  selectedEEComponentDetails,
  openSWModuleAddDialog,
  setOpenSWModuleAddDialog,
  handleSWModuleAdd,
  vfOptionsList,
  variantOptionList,
  selectedSingleVariantDetails,
  copySWModuleDetails,
  onCopyPrefillClear,
  viewArchived,
}) {
  const dispatch = useDispatch();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVfIds, setSelectedVfIds] = useState([]);
  const [selectedVariantsManySW, setSelectedVariantsManySW] = useState([]);
  const [supplierName, setSupplierName] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [githubLink, setGithubLink] = useState("");
  const [githubLinkError, setGithubLinkError] = useState("");
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [swmAddTabValue, setSwmAddTabValue] = useState(0);
  const copiedVfIdsForRender = useMemo(
    () =>
      (copySWModuleDetails?.vf_ids || [])
        ?.map(Number)
        ?.filter((value) => !Number.isNaN(value)),
    [copySWModuleDetails],
  );
  const shouldUseCopiedVfIds =
    openSWModuleAddDialog &&
    copySWModuleDetails &&
    selectedVfIds.length === 0 &&
    copiedVfIdsForRender.length > 0;
  const effectiveSelectedVfIds =
    shouldUseCopiedVfIds
      ? copiedVfIdsForRender
      : selectedVfIds;

  const handleAttributeFormData = useCallback((data = {}) => {
    const nextData = {
      added: Array.isArray(data?.added) ? data.added : [],
      edited: Array.isArray(data?.edited) ? data.edited : [],
      deleted: Array.isArray(data?.deleted) ? data.deleted : [],
    };

    setAttributeFormData((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
    );
  }, []);

  const addedAttributeFormRows = useMemo(
    () =>
      (Array.isArray(attributeFormData?.added)
        ? attributeFormData.added
        : []
      ).map((attribute) => ({
        ...attribute,
        attribute_id: null,
        newlyAdded: true,
        edited: false,
      })),
    [attributeFormData?.added],
  );

  const normalizeCopiedKeywordIds = (keywords) =>
    (Array.isArray(keywords) ? keywords : [])
      .map((keyword) =>
        keyword && typeof keyword === "object"
          ? keyword?.id ?? keyword?.value
          : keyword,
      )
      .filter(
        (id) => id !== null && id !== undefined && id !== "",
      );


  useEffect(() => {
    if (!openSWModuleAddDialog) return;

    dispatch(
      HandleApiActions({
        api_name: "/SCM/settings/view/keywords",
        https_method: "GET",
        params: { type: 1 },
        ignore_app_loading_spinner: true,
      }),
    ).then((response) => {
      const keywords = Array.isArray(response?.data) ? response.data : [];
      setKeywordOptions(
        keywords.map((keyword) => ({
          value: keyword?.id,
          label: keyword?.name,
        })),
      );
    });
  }, [dispatch, openSWModuleAddDialog]);

  useEffect(() => {
    if (!openSWModuleAddDialog || !copySWModuleDetails) return;

    const copiedVfIds = (copySWModuleDetails?.vf_ids || [])
      ?.map(Number)
      ?.filter((value) => !Number.isNaN(value));
    const copiedGitLink =
      copySWModuleDetails?.meta_details?.linked_meta_link ||
      copySWModuleDetails?.git_link ||
      "";
    const copiedAttributes = Array.isArray(copySWModuleDetails?.attributes)
      ? copySWModuleDetails.attributes.map((attribute) => ({
        attribute_type: attribute?.attribute_type,
        attribute_sub_type: attribute?.attribute_sub_type || null,
        attribute_heading: attribute?.attribute_heading,
        attribute_value: attribute?.attribute_value,
      }))
      : [];
    const copiedVariantIds = Array.isArray(copySWModuleDetails?.variant_ids)
      ? copySWModuleDetails.variant_ids
      : [];
    const currentVariantId = selectedSingleVariantDetails?.variant_id;
    const nextVariantIds = [
      ...new Set(
        [currentVariantId, ...copiedVariantIds].filter(
          (value) => value !== undefined && value !== null && value !== "",
        ),
      ),
    ];

    setName("");
    setNameError("");
    setVersion(copySWModuleDetails?.version || "");
    setVersionError("");
    setDescription(copySWModuleDetails?.description || "");
    setDescriptionError("");
    setSummary(copySWModuleDetails?.summary || "");
    setSummaryError("");
    setSelectedVfIds(copiedVfIds);
    setSelectedVariantsManySW(nextVariantIds);
    let initialSupplierName = [];
    if (Array.isArray(copySWModuleDetails?.supplier_name)) {
      initialSupplierName = copySWModuleDetails.supplier_name;
    } else if (copySWModuleDetails?.supplier_name) {
      initialSupplierName = [copySWModuleDetails.supplier_name];
    }
    setSupplierName(initialSupplierName);
    setSelectedKeywordIds(
      normalizeCopiedKeywordIds(copySWModuleDetails?.keywords),
    );
    setGithubLink(copiedGitLink);
    setGithubLinkError(getGithubLinkError(copiedGitLink));
    setAttributeFormData({
      added: copiedAttributes,
      edited: [],
      deleted: [],
    });
    setSwmAddTabValue(0);
  }, [
    copySWModuleDetails,
    openSWModuleAddDialog,
    selectedSingleVariantDetails?.variant_id,
  ]);


  const clearAddSWModuleFields = () => {
    setName("");
    setNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setSelectedVfIds([]);
    setSelectedVariantsManySW([]);
    setSupplierName([]);
    setSelectedKeywordIds([]);
    setGithubLink("");
    setGithubLinkError("");
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const isAddSWModuleValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      name?.trim() !== "" &&
      name?.trim()?.length >= 3 &&
      !nameError &&
      version?.trim() !== "" &&
      version?.trim()?.length >= 3 &&
      !versionError &&
      regexMin3?.test(version?.trim()) &&
      description?.trim() !== "" &&
      description?.trim()?.length >= 3 &&
      !descriptionError &&
      effectiveSelectedVfIds?.length > 0 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
      isGithubLinkValid(githubLink)
    );
  };

  const isAddSWModuleDraftValid = () => {
    const hasDraftValue =
      name?.trim() !== "" ||
      supplierName?.length > 0 ||
      version?.trim() !== "" ||
      description?.trim() !== "" ||
      summary?.trim() !== "" ||
      effectiveSelectedVfIds?.length > 0 ||
      selectedKeywordIds?.length > 0 ||
      githubLink?.trim() !== "";

    return hasDraftValue && isGithubLinkValid(githubLink);
  };

  const handleCloseSWModuleAdd = () => {
    setOpenSWModuleAddDialog(false);

    clearAddSWModuleFields();
    onCopyPrefillClear?.();
  };


  const handleAddSWModule = () => {
    handleSWModuleAdd({
      variant_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      name: name?.trim(),
      summary,
      description,
      supplier_name: supplierName,
      keywords: selectedKeywordIds?.map(Number) || [],
      vf_ids: effectiveSelectedVfIds?.map(Number) || [],
      ...getGithubLinkPayload(githubLink),
      attributes: attributeFormData?.added || [],
    });
    handleCloseSWModuleAdd();

  };

  const handleAddSWModuleDraft = () => {
    const reqObj = {
      ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),
      keywords: selectedKeywordIds?.map(Number) || [],
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedSingleVariantDetails?.variant_id,
      ee_id: selectedEEComponentDetails?.ee_id,
      ...(version?.length > 0 ? { version: version?.trim() } : null),
      name: name?.trim(),
      ...(summary?.length > 0 ? { summary: summary } : null),
      ...(description?.length > 0 ? { description: description } : null),
      ...(effectiveSelectedVfIds?.length > 0
        ? { vf_ids: effectiveSelectedVfIds?.map(Number) || [] }
        : null),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      save_as_draft: true,
      current_time: moment().format(),
      ...getGithubLinkPayload(githubLink),
      attributes: attributeFormData?.added || [],
    };

    dispatch(
      HandleApiActions({
        api_name: "/VLC/swm/add",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft S/W Component",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() =>
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: selectedProjectDetails?.project_id,
            variant_id: selectedSingleVariantDetails?.variant_id,
            ee_id: selectedEEComponentDetails?.ee_id,
            archive: !!viewArchived,
          },
          show_toast: false,
        }),
      ),
    );
    handleCloseSWModuleAdd();
  };

  useEffect(() => {
    customConsole({ selectedVfIds, vfOptionsList });
  }, [selectedVfIds, vfOptionsList]);

  useEffect(() => {
    const eeVariantIds = Array.isArray(selectedEEComponentDetails?.variant_ids)
      ? selectedEEComponentDetails.variant_ids
      : [];
    const optionIds = Array.isArray(variantOptionList)
      ? variantOptionList?.map((v) => v?.value)
      : [];
    const base = eeVariantIds.filter((id) => optionIds.includes(id));
    const currentId = selectedSingleVariantDetails?.variant_id;
    const merged = currentId ? [currentId, ...base] : base;
    const seen = new Set();
    const deduped = merged.filter((id) => {
      const key = String(id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    setSelectedVariantsManySW(deduped);
  }, [
    selectedEEComponentDetails,
    variantOptionList,
    selectedSingleVariantDetails,
  ]);

  useEffect(() => {
    if (
      openSWModuleAddDialog &&
      selectedSingleVariantDetails?.variant_id
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsManySW((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
    setSwmAddTabValue(0);
  }, [openSWModuleAddDialog, selectedSingleVariantDetails]);

  return (
    <Box>
      <CustomDialogBoxV2
        open={openSWModuleAddDialog}
        onClose={handleCloseSWModuleAdd}
        title="Add Software Module"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box>
              {swmAddTabValue === 1 ? (
                <CustomButtonV2
                  type="cancel"
                  size="small"
                  btnName={"Back"}
                  handleOnClick={() => setSwmAddTabValue(0)}
                />
              ) : null}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {swmAddTabValue === 1 ? (
                <CustomButtonV2
                  type="other"
                  size="small"
                  btnName={"Save Draft"}
                  handleOnClick={handleAddSWModuleDraft}
                  disabled={!isAddSWModuleDraftValid()}
                />
              ) : null}

              <CustomButtonV2
                type="submit"
                size="small"
                btnName={swmAddTabValue === 0 ? "Next" : "Add"}
                handleOnClick={() => {
                  if (swmAddTabValue === 0) {
                    setSwmAddTabValue(1);
                    return;
                  }
                  handleAddSWModule();
                }}
                disabled={
                  swmAddTabValue === 0
                    ? !isAddSWModuleDraftValid()
                    : !isAddSWModuleValid()
                }
              />
            </Box>
          </Box>
        }

      >
        <CustomTabsV2
          defaultTab={swmAddTabValue}
          value={swmAddTabValue}
          onTabChange={(newValue) => setSwmAddTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Box sx={{ width: "450px", minHeight: "600px" }}>
                  <Stack spacing={2} mb={2}>
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Number"}
                      required={true}
                      type={"text"}
                      value={name}
                      setValue={(value) =>
                        value?.length <= 40 && setName(value)
                      }
                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Version"}
                      required={true}
                      type={"text"}
                      fldType={"textR"}
                      value={version}
                      setValue={(value) =>
                        value?.length <= 40 && setVersion(value)
                      }
                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Description"}
                      required={true}
                      value={description}
                      setValue={(value) =>
                        value?.length <= 500 && setDescription(value)
                      }
                      maxLength={500}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Summary"}
                      value={summary}
                      setValue={(value) =>
                        value?.length <= 250 && setSummary(value)
                      }
                      maxLength={250}
                    />

                    <MultiselectConnectorV2
                      multiple
                      required={true}
                      selectedValues={supplierName || []}
                      setSelectedValues={setSupplierName}
                      label={"Select Supplier Name"}
                      optionsList={supplierOptionsList}
                      placeholder={false}
                    />

                    <MultiselectConnectorV2
                      size={"small"}
                      required
                      label={"Select Vehicle Functions"}
                      optionsList={selectedSingleVariantDetails?.vehicle_function_details?.map(
                        (el) => ({
                          value: el.id,
                          label: `${el.abbreviation} - ${el.name}`,
                        }),
                      )}
                      selectedValues={effectiveSelectedVfIds || " "}
                      setSelectedValues={setSelectedVfIds}
                      placeholder={false}
                    />
                    <CustomFormTextFieldV2
                      size="small"
                      label="GitHub Link"
                      placeholder="https://github.com/username/repo"
                      value={githubLink}
                      valueError={githubLinkError}
                      setValueError={setGithubLinkError}
                      setValue={(value) => {
                        if (value?.length <= 100) {
                          setGithubLink(value);
                          setGithubLinkError(getGithubLinkError(value));
                        }
                      }}
                      maxLength={100}
                    />

                    <MultiselectConnectorV2
                      multiple
                      selectedValues={selectedKeywordIds || []}
                      setSelectedValues={setSelectedKeywordIds}
                      label={"Keywords"}
                      optionsList={keywordOptions}
                      placeholder={false}
                    />
                    <MultiselectConnectorV2
                      disabled={true}
                      size={"small"}
                      multiple
                      selectedValues={selectedVariantsManySW || " "}
                      setSelectedValues={setSelectedVariantsManySW}
                      label={"Select Variants"}
                      optionsList={variantOptionList}
                      placeholder={false}
                      immutableValues={
                        selectedSingleVariantDetails?.variant_id
                          ? [selectedSingleVariantDetails?.variant_id]
                          : []
                      }
                    />
                  </Stack>
                </Box>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"ADD"}
                    attributeFromApi={addedAttributeFormRows}
                    getFormData={handleAttributeFormData}
                  />
                </Stack>
              ),
            },
          ]}
        />
      </CustomDialogBoxV2>
    </Box>
  );
}

VlcSWModuleAddDialog.propTypes = {
  supplierOptionsList: PropTypes.array,
  getSWModulesList: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_project_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    vehicle_function_details: PropTypes.array,
    is_user_project_owner: PropTypes.bool,
  }),
  selectedEEComponentDetails: PropTypes.shape({
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variant_ids: PropTypes.array,
    zone_details: PropTypes.shape({
      zone_name: PropTypes.string,
      zone_position_name: PropTypes.string,
    }),
  }),
  openSWModuleAddDialog: PropTypes.bool,
  setOpenSWModuleAddDialog: PropTypes.func,
  handleSWModuleAdd: PropTypes.func,
  vfOptionsList: PropTypes.array,
  variantOptionList: PropTypes.array,
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vehicle_function_details: PropTypes.array,
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  copySWModuleDetails: PropTypes.shape({
    name: PropTypes.string,
    version: PropTypes.string,
    description: PropTypes.string,
    summary: PropTypes.string,
    supplier_name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    keywords: PropTypes.array,
    vf_ids: PropTypes.array,
    variant_ids: PropTypes.array,
    attributes: PropTypes.array,
    git_link: PropTypes.string,
    meta_details: PropTypes.shape({
      linked_meta_link: PropTypes.string,
    }),
  }),
  onCopyPrefillClear: PropTypes.func,
  viewArchived: PropTypes.bool,
};

function VlcSWModuleUpdateDialog({
  variantOptionList,
  openSWModuleUpdateDialog,
  setOpenSWModuleUpdateDialog,
  selectedSWModuleUpdateDetails,
  setSelectedSWModuleUpdateDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  isSWMVersionImpactFlow,
  setIsSWMVersionImpactFlow,
  resumeSWMAttributeImpactFlowTrigger,
  resumeSWMVersionImpactFlowTrigger,
  onRedirectedActionDialogClose,
  viewArchived,
}) {
  const navigate = useNavigate();

  const [version, setVersion] = useState("");
  const [versionError, setVersionError] = useState("");
  const [showAttributeConfirmation, setShowAttributeConfirmation] =
    useState(false);
  const [openUpdateFlowAttributeDialog, setOpenUpdateFlowAttributeDialog] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const [
    isEditAttributeVersionDependencyFlow,
    setIsEditAttributeVersionDependencyFlow,
  ] = useState(false);
  const [
    isAttributeImpactUpdatePersisted,
    setIsAttributeImpactUpdatePersisted,
  ] = useState(false);
  const [discp, setDiscp] = useState("");
  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [selectedSWM, setSelectedSWM] = useState(null);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const [githubLink, setGithubLink] = useState("");
  const dispatch = useDispatch();

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetsList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  const [ruleSetTree, setRuleSetTree] = useState(null);
  const [rulePath, setRulePath] = useState([]);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);

  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const [disableActionButton, setDisableActionButton] = useState(false);
  const lastResumeSWMAttributeImpactFlowTriggerRef = useRef(0);
  const lastResumeSWMVersionImpactFlowTriggerRef = useRef(0);

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath?.length) return;

    const newPath = rulePath
      ?.slice(0, idx + 1)
      ?.map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else if (currentEntry?.node?.data?.category) {
      if (currentEntry?.node?.data?.requires_homologation) {
        void updateChangesAndClose();
        setRulePath(newPath);
        setShowRuleSetQuestionDialog(false);
        setRuleSetTree(null);
        setRulePath([]);
        setSelectedRuleSet("");
      } else {
        setRulePath(newPath);
        setShowRuleSetQuestionDialog(false);
        setRuleSetTree(null);
        setRulePath([]);
        setSelectedRuleSet("");
      }
    } else {
      setRulePath(newPath);
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);



  const clearUpdateSWModuleFields = () => {
    setVersion("");
    setVersionError("");
    setGithubLink("");
  };

  const isUpdateSWModuleValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;

    return (
      !versionError &&
      version?.trim() !== selectedSWModuleUpdateDetails?.version &&
      version?.trim()?.length >= 3 &&
      regexMin3?.test(version?.trim()) &&
      discp?.trim()?.length >= 3
    );
  };

  const handleCloseSWModuleUpdate = () => {
    setOpenSWModuleUpdateDialog(false);
    onRedirectedActionDialogClose?.();

    clearUpdateSWModuleFields();

    setSelectedSWModuleUpdateDetails(null);
    setShowAttributeConfirmation(false);
    setOpenUpdateFlowAttributeDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
    setIsSWMVersionImpactFlow(false);
    setIsEditAttributeVersionDependencyFlow(false);
    setIsAttributeImpactUpdatePersisted(false);
    setDisableActionButton(false);
  };

  useEffect(() => {
    if (
      resumeSWMAttributeImpactFlowTrigger &&
      resumeSWMAttributeImpactFlowTrigger !==
      lastResumeSWMAttributeImpactFlowTriggerRef.current &&
      selectedSWModuleUpdateDetails
    ) {
      lastResumeSWMAttributeImpactFlowTriggerRef.current =
        resumeSWMAttributeImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsSWMVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsAttributeImpactUpdatePersisted(true);
      setTimeout(() => {
        setOpenUpdateFlowAttributeDialog(true);
      }, 200);
    }
  }, [resumeSWMAttributeImpactFlowTrigger, selectedSWModuleUpdateDetails, setIsSWMVersionImpactFlow]);

  useEffect(() => {
    if (
      resumeSWMVersionImpactFlowTrigger &&
      resumeSWMVersionImpactFlowTrigger !==
      lastResumeSWMVersionImpactFlowTriggerRef.current &&
      selectedSWModuleUpdateDetails
    ) {
      lastResumeSWMVersionImpactFlowTriggerRef.current =
        resumeSWMVersionImpactFlowTrigger;
      setShowAttributeConfirmation(false);
      setOpenUpdateFlowAttributeDialog(false);
      setShowConfirmation(false);
      setShowIntersectionImpactPrompt(false);
      setIsAttributeImpactUpdatePersisted(false);
      setIsEditAttributeVersionDependencyFlow(false);
      setIsSWMVersionImpactFlow(true);
    }
  }, [resumeSWMVersionImpactFlowTrigger, selectedSWModuleUpdateDetails, setIsSWMVersionImpactFlow]);

  const handleUpdateClick = () => {
    if (isSWMVersionImpactFlow) {
      void updateVersionImpactSWModule();
      return;
    }

    setShowAttributeConfirmation(true);
  };

  const updateVersionImpactSWModule = async () => {
    setDisableActionButton(true);

    try {
      const reqObj = {
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleUpdateDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        version: version?.trim(),
        ...(githubLink ? { git_link: githubLink } : null),
        will_it_impact: true,
        variant_ids: selectedEEComponentDetails?.variant_ids,
        reason: discp,
      };

      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (response?.error) {
        setDisableActionButton(false);
        return;
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(selectedSWModuleUpdateDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
              swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
              is_dependency_linking: false,
              dependency: true,
            },
            show_toast: false,
          }),
        ),
      );

      setIsSWMVersionImpactFlow(false);
      setIsEditAttributeVersionDependencyFlow(false);
      handleCloseSWModuleUpdate();
      setSelectedSWM(selectedSWModuleUpdateDetails);
      setActionType1DialogOpen(true);
      getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update action type", error);
    }

    setDisableActionButton(false);
  };

  const handleEditAttributeVersionDependencyYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect({
      selectedSWModuleUpdateDetails,
    });
  };

  const handleEditAttributeVersionIntersectionNo = async () => {
    setShowIntersectionImpactPrompt(false);
    setIsEditAttributeVersionDependencyFlow(false);
    await updateChangesWithoutStandardAndClose();
    handleCloseSWModuleUpdate();
  };

  const handleEditAttributeVersionIntersectionYes = async () => {
    setIsEditAttributeVersionDependencyFlow(false);
    await confirmUpdateAndRedirect({
      selectedSWModuleUpdateDetails,
      willImptIntrOnly: true,
    });
  };

  const handlePersistedAttributeDependencyYes = async () => {
    setDisableActionButton(true);
    setShowConfirmation(false);

    try {
      const updateSWModulePayload = {
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleUpdateDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        version: version?.trim(),
        ...(githubLink ? { git_link: githubLink } : null),
        will_it_impact: true,
        variant_ids: selectedEEComponentDetails?.variant_ids,
        reason: discp,
      };

      const updateResponse = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: updateSWModulePayload,
            show_toast: true,
          }),
        ),
      );

      if (updateResponse?.error) {
        setDisableActionButton(false);
        return;
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(selectedSWModuleUpdateDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
              swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
              is_dependency_linking: false,
              dependency: true,
            },
            show_toast: false,
          }),
        ),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseSWModuleUpdate();
      getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const handlePersistedAttributeIntersectionNo = () => {
    setShowIntersectionImpactPrompt(false);
    setIsAttributeImpactUpdatePersisted(false);
    handleCloseSWModuleUpdate();
  };

  const handlePersistedAttributeIntersectionYes = async () => {
    setDisableActionButton(true);
    setShowIntersectionImpactPrompt(false);

    try {
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(selectedSWModuleUpdateDetails?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
              swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
              is_dependency_linking: false,
              intersection: true,
            },
            show_toast: false,
          }),
        ),
      );

      setIsAttributeImpactUpdatePersisted(false);
      handleCloseSWModuleUpdate();
      getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update action type", error);
      setDisableActionButton(false);
    }
  };

  const confirmUpdateAndRedirect = async ({
    selectedSWModuleUpdateDetails,
    willImptIntrOnly = false,
  }) => {
    setDisableActionButton(true);
    const reqObj = {
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version?.trim(),
      ...(githubLink ? { git_link: githubLink } : null),
      will_it_impact: true,
      variant_ids: selectedEEComponentDetails?.variant_ids,
      reason: discp,
    };
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      if (!response.error) {
        handleCloseSWModuleUpdate();
        willImptIntrOnly
          ? handleActionType2DialogOpen()
          : handleActionType1DialogOpen();
        if (willImptIntrOnly) {
          await Promise.resolve(
            dispatch(
              HandleApiActions({
                ...labCar.update_action_type,
                params: {
                  current_time: new Date().toISOString(),
                  project_id: Number(selectedSWModuleUpdateDetails?.project_id),
                  variant_id: Number(selectedSingleVariantDetails?.variant_id),
                  ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                  swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
                  is_dependency_linking: true,
                },
                show_toast: false,
              }),
            ),
          );
        }
        await getSWModulesList({ force: true });
      }
    } catch (error) {
      console.error("Failed to update", error);
    }
    setDisableActionButton(false);
  };

  const handleCloseRuleSetListDialog = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseSWModuleUpdate();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_set_tree,
            params: {
              rule_set_id: ruleSetIdToUse,
            },
            show_toast: false,
          }),
        ),
      );

      if (response?.data) {
        setRuleSetTree(response.data);
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch rule set questions", error);
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: false,
      reason: discp,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (response?.data?.snow_resp) {
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }

      await getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const updateChangesAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleUpdateDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
      swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
      version: version?.trim(),
      will_it_impact: false,
      requires_homologation: false,
      reason: discp,
    };
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      if (!response.error) {
        handleCloseSWModuleUpdate();
      }
      if (response?.data?.snow_resp) {
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }
      await getSWModulesList({ force: true });
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleActionType1DialogOpen = () => {
    setSelectedSWM(selectedSWModuleUpdateDetails);
    setActionType1DialogOpen(true);
  };
  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogOpen = () => {
    setSelectedSWM(selectedSWModuleUpdateDetails);
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  useEffect(() => {
    if (openSWModuleUpdateDialog) {
      setDiscp("");
    }
  }, [openSWModuleUpdateDialog]);

  useEffect(() => {
    if (openSWModuleUpdateDialog && selectedSWModuleUpdateDetails) {
      setVersion(selectedSWModuleUpdateDetails?.version || "");
    }
  }, [openSWModuleUpdateDialog, selectedSWModuleUpdateDetails]);

  const loadDecisionMatrixContext = async () => {
    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "SWM",
            component_id: selectedSWModuleUpdateDetails.swm_id,
            project_id: selectedProjectDetails.project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
          },
          show_toast: false,
        }),
      ),
    );
    setOpenSWModuleUpdateDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload.workflowId,
              version: payload.version,
            },
            show_toast: false,
          }),
        ),
      );

      setExecutionId(res.executionId);
      return res;
    }

    if (mode === "COMPLETE") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_node_complete,
            params: {
              executionId: payload.executionId,
              nodeId: payload.nodeId,
              input: payload.input,
            },
            show_toast: false,
          }),
        ),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
  };
  const updateComponentAndCheckForDecisionMatrix = async ({
    standardCode,
  } = {}) => {
    try {


      const reCreateTicketParams = {
        details: createTicketData,
        reason: discp,
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.re_create_ticket_v1,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      if (!res?.error && standardCode) {
        handleTicketCreationSuccess(standardCode);
      }

      return res;

    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handleTicketCreationSuccess = (standardCode) => {
    setDisabledStandards((prev) => [...new Set([...prev, standardCode])]);
  };

  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "swm",
        entity_id: Number(selectedSWModuleUpdateDetails?.swm_id),
        ...(standard_name ? { standard_name } : {}),
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_similar_tickets,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "SW Module",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
  };
  return (
    <Box>


      <CustomDialogBoxV2
        open={openSWModuleUpdateDialog}
        onClose={handleCloseSWModuleUpdate}
        title={"Update Software Module"}
        dialogActions={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <CustomButtonV2
              btnName={"Cancel"}
              size="small"
              type="cancel"
              handleOnClick={handleCloseSWModuleUpdate}
            />
            <CustomButtonV2
              btnName={"Update"}
              size="small"
              type="submit"
              handleOnClick={handleUpdateClick}
              disabled={!isUpdateSWModuleValid()}
            />
          </Box>
        }
      >
        <Stack spacing={2} mb={2} sx={{ width: "350px", height: "150px" }}>
          <CustomFormTextFieldV2
            size={"small"}
            label={"Version"}
            required={true}
            type={"text"}
            fldType="textR"
            value={version}
            setValue={(value) => value?.length <= 40 && setVersion(value)}
            maxLength={40}
          />

          <CustomFormTextFieldV2
            size={"small"}
            label={"Reason"}
            required={true}
            value={discp}
            setValue={(value) => value?.length <= 100 && setDiscp(value)}
            maxLength={100}
          />
          <CustomDialogBoxV2
            open={showAttributeConfirmation}
            onClose={() => {
              setShowAttributeConfirmation(false);
            }}
            title="Action"
            dialogActions={
              <Box
                sx={{
                  display: "flex",
                  width: "350px",
                  justifyContent: "space-between",
                  height: "50px",
                }}
              >
                <CustomButtonV2
                  size="small"
                  type="cancel"
                  btnName="No"
                  handleOnClick={() => {
                    setShowAttributeConfirmation(false);

                    setTimeout(() => {
                      setShowConfirmation(true);
                    }, 200);
                  }}
                />
                <CustomButtonV2
                  size="small"
                  type="submit"
                  btnName="Yes"
                  handleOnClick={async () => {
                    setDisableActionButton(true);
                    setShowAttributeConfirmation(false);

                    try {
                      const reqObj = {
                        current_time: new Date().toISOString(),
                        project_id: Number(
                          selectedSWModuleUpdateDetails?.project_id,
                        ),
                        variant_id: Number(
                          selectedSingleVariantDetails?.variant_id,
                        ),
                        ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                        swm_id: Number(selectedSWModuleUpdateDetails?.swm_id),
                        version: version?.trim(),
                        ...(githubLink ? { git_link: githubLink } : null),
                        will_it_impact: true,
                        variant_ids: selectedEEComponentDetails?.variant_ids,
                        reason: discp,
                      };

                      const response = await Promise.resolve(
                        dispatch(
                          HandleApiActions({
                            ...labCar.update_sw_module,
                            params: reqObj,
                            show_toast: true,
                          }),
                        ),
                      );

                      if (response?.error) {
                        setDisableActionButton(false);
                        return;
                      }
                      setOpenSWModuleUpdateDialog(false);
                      setIsAttributeImpactUpdatePersisted(true);

                      await Promise.resolve(
                        dispatch(
                          HandleApiActions({
                            ...labCar.update_action_type,
                            params: {
                              current_time: new Date().toISOString(),
                              project_id: Number(
                                selectedSWModuleUpdateDetails?.project_id,
                              ),
                              variant_id: Number(
                                selectedSingleVariantDetails?.variant_id,
                              ),
                              ee_id: Number(selectedSWModuleUpdateDetails?.ee_id),
                              swm_id: Number(
                                selectedSWModuleUpdateDetails?.swm_id,
                              ),
                              is_dependency_linking: false,
                              attribute: true,
                            },
                            show_toast: false,
                          }),
                        ),
                      );

                      setTimeout(() => {
                        setOpenUpdateFlowAttributeDialog(true);
                      }, 200);
                      getSWModulesList({ force: true });
                    } catch (error) {
                      console.error("Failed to update action type", error);
                    }

                    setDisableActionButton(false);
                  }}
                  disabled={disableActionButton}
                />
              </Box>
            }
          >
            <Box
              sx={{
                width: "350px",
                height: "50px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                }}
              >
                Will it impact the existing attributes?
              </Typography>
            </Box>
          </CustomDialogBoxV2>

        </Stack>
      </CustomDialogBoxV2>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openUpdateFlowAttributeDialog}
        setOpenAttributeUpdateDialog={setOpenUpdateFlowAttributeDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenUpdateFlowAttributeDialog(false);
          onRedirectedActionDialogClose?.();
        }}
        onClickSuccess={() => {
          getSWModulesList({ force: true });
          setOpenUpdateFlowAttributeDialog(false);
        }}
        selectedComponent={selectedSWModuleUpdateDetails}
        componentType={"swm"}
        isAttributeImpactFlow
        variantId={selectedSingleVariantDetails?.variant_id}
        onIntersectionNo={loadDecisionMatrixContext}
        attributeImpactActionType={{ dependency: true }}
        onAttributeImpactComplete={() => {
          getSWModulesList({ force: true });
          setOpenUpdateFlowAttributeDialog(false);
          setSelectedSWM(selectedSWModuleUpdateDetails);
          setActionType1DialogOpen(true);
        }}
      />

      <CustomDialogBoxV2
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
        }}
        title={"Action"}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <CustomButtonV2
              btnName={"No"}
              size="small"
              type="cancel"
              handleOnClick={() => {
                setShowConfirmation(false);

                setTimeout(() => {
                  setShowIntersectionImpactPrompt(true);
                }, 200);
              }}
            />
            <CustomButtonV2
              btnName={"Yes"}
              size="small"
              type="submit"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  void handlePersistedAttributeDependencyYes();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  void handleEditAttributeVersionDependencyYes();
                  return;
                }

                void confirmUpdateAndRedirect({
                  selectedSWModuleUpdateDetails,
                });
              }}
              disabled={disableActionButton}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
            }}
          >
            Will it impact the existing dependencies?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={showIntersectionImpactPrompt}
        onClose={() => {
          setShowIntersectionImpactPrompt(false);
        }}
        title="Action"
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="No"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  handlePersistedAttributeIntersectionNo();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  void handleEditAttributeVersionIntersectionNo();
                  return;
                }

                void loadDecisionMatrixContext();
                void updateChangesWithoutStandardAndClose();
              }}
            />
            <CustomButtonV2
              size="small"
              type="submit"
              btnName="Yes"
              handleOnClick={() => {
                if (isAttributeImpactUpdatePersisted) {
                  void handlePersistedAttributeIntersectionYes();
                  return;
                }

                if (isEditAttributeVersionDependencyFlow) {
                  void handleEditAttributeVersionIntersectionYes();
                  return;
                }

                void confirmUpdateAndRedirect({
                  selectedSWModuleUpdateDetails,
                  willImptIntrOnly: true,
                });
              }}
            />
          </Box>
        }
      >
        <Box
          sx={{
            width: "350px",
            height: "50px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              ...componentStyle.font.text,
            }}
          >
            Will it impact the existing intersection?
          </Typography>
        </Box>
      </CustomDialogBoxV2>

      <Box>

        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${selectedSWM?.swm_id}&ee_id=${selectedEEComponentDetails?.ee_id
              }&selectedZone=${selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>


        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id
              }&zone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id
              }&tab=${3}`,
            );
            setActionType2DialogOpen(false);
          }} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography sx={{ fontSize: "15px" }}>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>


        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets?tab=${1}`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>


        <CustomDialogBoxV2
          open={showRuleSetListDialog}
          onClose={handleCloseRuleSetListDialog}
          title="Select Rule Set"
          onClickSuccess={async () => {
            await viewRuleSetQuestions(selectedRuleSet);
            setShowRuleSetListDialog(false);
          }}
          onDisabled={!selectedRuleSet}
          onClickReject={handleCloseRuleSetListDialog}
          actionButtonStyle={"continuous"}
          successButtonName={"Confirm"}
          rejectButtonName={"Cancel"}
        >
          <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
            <CustomSelectV2
              size={"small"}
              label={"Select Rule Set"}
              required={true}
              optionsList={
                ruleSetsList?.map((rs) => ({
                  value: rs?.rule_set_id || rs?.id,
                  label: rs?.name,
                })) || []
              }
              selectedValue={selectedRuleSet || " "}
              handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
            />
          </Stack>
        </CustomDialogBoxV2>



        <CustomDialogBoxV2
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
          title={`Rule Set: ${ruleSetTree?.name || ""}`}
          contentSx={{ minHeight: "350px", width: "450px" }}

        >
          <DialogContent>
            <Stack spacing={2} sx={{}}>
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  const itemKey = entry.node?.id || entry.node?.rule_set_id || `rule-path-${idx}`;
                  return (
                    <Box
                      key={itemKey}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath?.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {isCategory ? (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box>
                              <CustomButtonV2
                                btnName={"Yes"}
                                handleOnClick={() => handleAnswerAt(idx, "yes")}
                                type={entry.answer === "yes" ? "submit" : "cancel"}
                              />
                            </Box>
                            <Box>
                              <CustomButtonV2
                                btnName={"No"}
                                handleOnClick={() => handleAnswerAt(idx, "no")}
                                type={entry.answer === "no" ? "submit" : "cancel"}
                              />
                            </Box>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {rulePath?.length > 0 &&
                  rulePath[rulePath?.length - 1]?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <CustomButtonV2
                      type="submit"
                      btnName={"Finish"}
                      handleOnClick={() => {
                        void updateChangesAndClose();
                        setShowRuleSetQuestionDialog(false);
                        setRuleSetTree(null);
                        setRulePath([]);
                        setSelectedRuleSet("");
                        handleCloseSWModuleUpdate();
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </DialogContent>
        </CustomDialogBoxV2>

        <Dialog
          open={showDecisionMatrix}
          onClose={handelCloseDecisionMatrix}
          fullScreen
          slotProps={{
            paper: {
              sx: {
                width: "100vw",
                height: "100vh",
                margin: 0,
                borderRadius: 0,
              },
            },
          }}
        >
          <AppBar
            position="sticky"
            sx={{
              backgroundColor: "#26890D",
            }}
          >
            <Toolbar>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Decision Matrix
              </Typography>
              <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flex: 1,
              height: "calc(100vh - 64px)",
              overflow: "auto",
              p: 2,
            }}
          >
            {showDecisionMatrix && (
              <DecisionMatrixPanel
                context={decisionMatrixContext}
                key={showDecisionMatrix ? "open" : "closed"}
                onExecuteWorkflow={handleExecuteWorkflow}
                onViewExecution={handleViewExecution}
                onClose={handelCloseDecisionMatrix}
                onDecisionMatrixAction={
                  updateComponentAndCheckForDecisionMatrix
                }
                handelGetSimilerTickets={handelGetSimilerTickets}
                similarTickets={similarTickets}
                setSimilarTickets={setSimilarTickets}
                entityDetails={selectedEntity}
                handelGetAiSuggestion={handelGetAiSuggestion}
                selectedTicket={selectedTicket}
                setSelectedTicket={setSelectedTicket}
                openAiPanel={openAiPanel}
                setOpenAiPanel={setOpenAiPanel}
                setCreateTicketData={setCreateTicketData}
                disabledStandards={disabledStandards}
              />
            )}
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
}

VlcSWModuleUpdateDialog.propTypes = {
  variantOptionList: PropTypes.array,
  openSWModuleUpdateDialog: PropTypes.bool,
  setOpenSWModuleUpdateDialog: PropTypes.func,
  selectedSWModuleUpdateDetails: PropTypes.shape({
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    version: PropTypes.string,
    description: PropTypes.string,
    summary: PropTypes.string,
    supplier_name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    keywords: PropTypes.array,
    vf_ids: PropTypes.array,
    variant_ids: PropTypes.array,
    attributes: PropTypes.array,
    git_link: PropTypes.string,
    meta_details: PropTypes.shape({
      linked_meta_link: PropTypes.string,
    }),
  }),
  setSelectedSWModuleUpdateDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  selectedZone: PropTypes.shape({
    zone_position_name: PropTypes.string,
  }),
  selectedEEComponentDetails: PropTypes.shape({
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variant_ids: PropTypes.array,
    zone_details: PropTypes.shape({
      zone_name: PropTypes.string,
      zone_position_name: PropTypes.string,
    }),
  }),
  getSWModulesList: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_project_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    vehicle_function_details: PropTypes.array,
    is_user_project_owner: PropTypes.bool,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vehicle_function_details: PropTypes.array,
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  isSWMVersionImpactFlow: PropTypes.bool,
  setIsSWMVersionImpactFlow: PropTypes.func,
  resumeSWMAttributeImpactFlowTrigger: PropTypes.number,
  resumeSWMVersionImpactFlowTrigger: PropTypes.number,
  onRedirectedActionDialogClose: PropTypes.func,
  viewArchived: PropTypes.bool,
};

// ---------------------------------------------------------------------------
// Pure utility helpers – kept at module scope to reduce the cognitive
// complexity of VlcSWModuleReplaceDialog (SonarLint S3776).
// ---------------------------------------------------------------------------
function normalizeArrayValue(value) {
  if (Array.isArray(value)) {
    return value.filter(
      (item) => item !== null && item !== undefined && item !== "",
    );
  }
  return value !== null && value !== undefined && value !== "" ? [value] : [];
}

function normalizeKeywordIds(keywords) {
  return (Array.isArray(keywords) ? keywords : [])
    .map((keyword) =>
      keyword && typeof keyword === "object"
        ? keyword?.id ?? keyword?.value
        : keyword,
    )
    .filter((id) => id !== null && id !== undefined && id !== "");
}

function haveArrayValuesChanged(nextValues = [], existingValues = []) {
  const next = normalizeArrayValue(nextValues).map(String).sort((a, b) => a.localeCompare(b));
  const existing = normalizeArrayValue(existingValues).map(String).sort((a, b) => a.localeCompare(b));
  return (
    next.length !== existing.length ||
    next.some((value, index) => value !== existing[index])
  );
}

function validateReplaceSWModuleDetails({
  name,
  nameError,
  version,
  versionError,
  description,
  descriptionError,
  summary,
  summaryError,
  supplierName,
  selectedVfIds,
  selectedKeywordIds,
  selectedSWModuleReplaceDetails,
  githubLink,
}) {
  const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
  return (
    !nameError &&
    !versionError &&
    !descriptionError &&
    supplierName?.length > 0 &&
    selectedVfIds?.length > 0 &&
    regexMin3?.test(version?.trim()) &&
    (name?.trim() !== selectedSWModuleReplaceDetails?.name ||
      haveArrayValuesChanged(
        supplierName,
        selectedSWModuleReplaceDetails?.supplier_name,
      ) ||
      haveArrayValuesChanged(
        selectedKeywordIds,
        normalizeKeywordIds(selectedSWModuleReplaceDetails?.keywords),
      )) &&
    (name?.trim() !== selectedSWModuleReplaceDetails?.name ||
      haveArrayValuesChanged(
        supplierName,
        selectedSWModuleReplaceDetails?.supplier_name,
      ) ||
      version?.trim() !== selectedSWModuleReplaceDetails?.version ||
      description?.trim() !== selectedSWModuleReplaceDetails?.description ||
      summary?.trim() !== selectedSWModuleReplaceDetails?.summary ||
      haveArrayValuesChanged(
        selectedKeywordIds,
        normalizeKeywordIds(selectedSWModuleReplaceDetails?.keywords),
      ) ||
      haveArrayValuesChanged(
        selectedVfIds,
        selectedSWModuleReplaceDetails?.vf_ids,
      )) &&
    name?.trim()?.length >= 3 &&
    version?.trim()?.length >= 3 &&
    description?.trim()?.length >= 3 &&
    (summary?.trim()?.length === 0 || !summaryError) &&
    (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
    isGithubLinkValid(githubLink)
  );
}

function validateUpdateDraftSWComponent({
  name,
  nameError,
  version,
  versionError,
  description,
  descriptionError,
  summary,
  summaryError,
  supplierName,
  selectedVfIds,
  githubLink,
}) {
  const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
  return (
    !nameError &&
    !versionError &&
    !descriptionError &&
    selectedVfIds?.length > 0 &&
    name?.trim()?.length >= 3 &&
    supplierName?.length > 0 &&
    version?.trim() !== "" &&
    version?.trim()?.length >= 3 &&
    !versionError &&
    regexMin3?.test(version?.trim()) &&
    description?.trim() !== "" &&
    description?.trim()?.length >= 3 &&
    (summary?.trim()?.length === 0 || !summaryError) &&
    (summary?.trim()?.length === 0 || summary?.trim()?.length >= 3) &&
    isGithubLinkValid(githubLink)
  );
}

function validateUpdateAgainDraftSWComponent({
  name,
  version,
  description,
  summary,
  supplierName,
  selectedVfIds,
  selectedKeywordIds,
  githubLink,
}) {
  const hasDraftValue =
    name?.trim() !== "" ||
    supplierName?.length > 0 ||
    version?.trim() !== "" ||
    description?.trim() !== "" ||
    summary?.trim() !== "" ||
    selectedVfIds?.length > 0 ||
    selectedKeywordIds?.length > 0 ||
    githubLink?.trim() !== "";

  return hasDraftValue && isGithubLinkValid(githubLink);
}

function getReplaceDialogDisabledState({
  title,
  swmTabValue,
  isReplaceDetailsValid,
  isReplaceValid,
  isUpdateAgainDraftValid,
  isUpdateDraftValid,
}) {
  const isReplace =
    title === "Replace Software Module" || title === "Draft Software Module";
  if (isReplace) {
    return swmTabValue === 0 ? !isReplaceDetailsValid : !isReplaceValid;
  }
  return swmTabValue === 0 ? !isUpdateAgainDraftValid : !isUpdateDraftValid;
}

function getReplaceDialogButtonConfig({
  title,
  swmTabValue,
  isUpdateAgainDraftValid,
  handleClose,
  handleUpdateAgainDraft,
}) {
  const isReplace = title === "Replace Software Module";
  const isDraftSWM = title === "Draft Software Module";

  let successButtonName = "Add";
  if (swmTabValue === 0) {
    successButtonName = "Next";
  } else if (isReplace) {
    successButtonName = "Replace";
  }

  let rejectButtonName = null;
  if (isReplace || isDraftSWM) {
    rejectButtonName = "Cancel";
  } else if (swmTabValue === 1) {
    rejectButtonName = "Save Draft";
  }

  const rejectDisabled = isReplace || isDraftSWM ? false : !isUpdateAgainDraftValid;
  const rejectButtonType = isReplace || isDraftSWM ? "cancel" : "other";
  const onClickReject = isReplace || isDraftSWM ? handleClose : handleUpdateAgainDraft;

  const isBackAllowed =
    (isReplace || isDraftSWM || title === "Add Draft Software Module") &&
    swmTabValue === 1;
  const otherButtonName = isBackAllowed ? "Back" : null;

  return {
    successButtonName,
    rejectButtonName,
    rejectDisabled,
    rejectButtonType,
    onClickReject,
    otherButtonName,
  };
}
function buildReplaceSWModulePayload({
  selectedSWModuleReplaceDetails,
  selectedSingleVariantDetails,
  selectedEEComponentDetails,
  supplierName,
  selectedKeywordIds,
  newEEId,
  isDraftSWM,
  isEEChanged,
  version,
  name,
  summary,
  description,
  selectedVfIds,
  attributeFormData,
  discp,
  githubLink,
}) {
  return {
    ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),
    keywords: selectedKeywordIds?.map(Number) || [],
    current_time: new Date().toISOString(),
    project_id: Number(selectedSWModuleReplaceDetails?.project_id),
    variant_id: Number(selectedSingleVariantDetails?.variant_id),
    ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
    ...(isDraftSWM && isEEChanged && { new_ee_id: newEEId }),
    swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
    variants_ids: selectedEEComponentDetails?.variant_ids,
    version: version?.trim(),
    name: name?.trim(),
    ...(summary && { summary }),
    description,
    vf_ids: selectedVfIds,
    attributes: attributeFormData?.added || [],
    will_it_impact: !isDraftSWM,
    reason: discp,
    ...getGithubLinkPayload(githubLink),
  };
}

// ---------------------------------------------------------------------------

function VlcSWModuleReplaceDialog({
  openSWModuleReplaceDialog,
  setOpenSWModuleReplaceDialog,
  selectedSWModuleReplaceDetails,
  setSelectedSWModuleReplaceDetails,
  vfOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  title,
  supplierOptionsList,
  viewArchived,
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [version, setVersion] = useState("");
  const [supplierName, setSupplierName] = useState([]);
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError, setSummaryError] = useState("");
  const [selectedVfIds, setSelectedVfIds] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false); // State to track confirmation dialog
  const [discp, setDiscp] = useState("");
  const [discpError, setDiscpError] = useState("");
  const [selectedSWM, setSelectedSWM] = useState(null);

  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const [githubLink, setGithubLink] = useState("");
  const [githubLinkError, setGithubLinkError] = useState("");
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });

  const handleAttributeFormData = useCallback((data = {}) => {
    const nextData = {
      added: Array.isArray(data?.added) ? data.added : [],
      edited: Array.isArray(data?.edited) ? data.edited : [],
      deleted: Array.isArray(data?.deleted) ? data.deleted : [],
    };

    setAttributeFormData((prev) =>
      JSON.stringify(prev) === JSON.stringify(nextData) ? prev : nextData,
    );
  }, []);

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetsList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  const [ruleSetTree, setRuleSetTree] = useState(null);
  const [rulePath, setRulePath] = useState([]);
  const [selectedVariantsManySW, setSelectedVariantsManySW] = useState([]);
  const [showDecisionMatrix, setShowDecisionMatrix] = useState(false);
  const [decisionMatrixContext, setDecisionMatrixContext] = useState(null);
  const [executionId, setExecutionId] = useState(null);

  const [similarTickets, setSimilarTickets] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openAiPanel, setOpenAiPanel] = useState(false);
  const [createTicketData, setCreateTicketData] = useState({});
  const [disabledStandards, setDisabledStandards] = useState([]);
  const [disableActionButton, setDisableActionButton] = useState(false);
  const [swmTabValue, setSwmTabValue] = useState(0);
  const [selectedZoneName, setSelectedZoneName] = useState("");
  const [selectedEEId, setSelectedEEId] = useState("");
  const [eeComponentOptions, setEeComponentOptions] = useState([]);

  const closeRuleSetQuestionDialog = (newPath) => {
    setRulePath(newPath);
    setShowRuleSetQuestionDialog(false);
    setRuleSetTree(null);
    setRulePath([]);
    setSelectedRuleSet("");
  };

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath?.length) return;

    const newPath = rulePath
      ?.slice(0, idx + 1)
      ?.map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[answer + "_branch"];

    if (nextBranch) {
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
      return;
    }

    if (currentEntry?.node?.data?.category) {
      if (currentEntry?.node?.data?.requires_homologation) {
        void replaceChangesAndClose();
      }
      closeRuleSetQuestionDialog(newPath);
    } else {
      setRulePath(newPath);
    }
  };
  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);

  const dispatch = useDispatch();


  useEffect(() => {
    if (openSWModuleReplaceDialog) {
      setDiscp("");
    }
  }, [openSWModuleReplaceDialog]);

  useEffect(() => {
    if (!openSWModuleReplaceDialog) return;

    dispatch(
      HandleApiActions({
        api_name: "/SCM/settings/view/keywords",
        https_method: "GET",
        params: { type: 1 },
        ignore_app_loading_spinner: true,
      }),
    ).then((response) => {
      const keywords = Array.isArray(response?.data) ? response.data : [];
      setKeywordOptions(
        keywords.map((keyword) => ({
          value: keyword?.id,
          label: keyword?.name,
        })),
      );
    });
  }, [dispatch, openSWModuleReplaceDialog]);


  const [zoneEEHistory, setZoneEEHistory] = useState({});
  const zoneEEHistoryRef = useRef({});
  zoneEEHistoryRef.current = zoneEEHistory;

  useEffect(() => {
    if (openSWModuleReplaceDialog === true && selectedSWModuleReplaceDetails) {
      setName(selectedSWModuleReplaceDetails?.name || "");
      setVersion(selectedSWModuleReplaceDetails?.version || "");
      setDescription(selectedSWModuleReplaceDetails?.description || "");
      setSupplierName(
        normalizeArrayValue(selectedSWModuleReplaceDetails?.supplier_name),
      );
      setSelectedKeywordIds(
        normalizeKeywordIds(selectedSWModuleReplaceDetails?.keywords),
      );
      setSummary(selectedSWModuleReplaceDetails?.summary || "");
      setSelectedVfIds(
        (selectedSWModuleReplaceDetails?.vf_ids || [])
          ?.map(Number)
          ?.filter((vfId) => !Number.isNaN(vfId)),
      );
      setGithubLink(
        selectedSWModuleReplaceDetails?.meta_details?.linked_meta_link ||
        selectedSWModuleReplaceDetails?.git_link ||
        "",
      );
      setGithubLinkError(
        getGithubLinkError(
          selectedSWModuleReplaceDetails?.meta_details?.linked_meta_link ||
          selectedSWModuleReplaceDetails?.git_link ||
          "",
        ),
      );
      const initialZone =
        selectedSWModuleReplaceDetails?.zone_details?.zone_name ||
        selectedProjectDetails?.project_zones?.find(
          (z) => z?.position === selectedSWModuleReplaceDetails?.zone_position,
        )?.zone_name ||
        selectedZone?.zone_position_name ||
        "";
      const initialEEId = selectedSWModuleReplaceDetails?.ee_id
        ? String(selectedSWModuleReplaceDetails.ee_id)
        : "";
      setSelectedZoneName(initialZone);
      setSelectedEEId(initialEEId);
      if (initialZone && initialEEId) {
        setZoneEEHistory({ [initialZone]: initialEEId });
      } else {
        setZoneEEHistory({});
      }
    }
    setSwmTabValue(0);
  }, [
    openSWModuleReplaceDialog,
    selectedSWModuleReplaceDetails,
    selectedProjectDetails,
    selectedZone,
  ]);

  useEffect(() => {
    if (!openSWModuleReplaceDialog) return;

    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    if (!projectId || !variantId) {
      setEeComponentOptions([]);
      return;
    }

    const isZonal = !!selectedProjectDetails?.is_zonal;
    let zonePosition;
    if (isZonal) {
      if (!selectedZoneName) {
        setEeComponentOptions([]);
        return;
      }
      const matchedZone = selectedProjectDetails?.project_zones?.find(
        (z) => z?.zone_name === selectedZoneName || z?.position === selectedZoneName,
      );
      zonePosition = matchedZone?.position;
    }

    const isDraftDialog =
      title === "Draft Software Module" ||
      title === "Add Draft Software Module" ||
      title?.toLowerCase()?.includes("draft");

    const params = {
      project_id: projectId,
      variant_id: variantId,
      page_number: 1,
      page_size: 1000,
      archive: isDraftDialog ? false : !!viewArchived,
      ...(isZonal && zonePosition !== undefined && { zone_position: zonePosition }),
    };

    dispatch(
      HandleApiActions({
        ...labCar.get_ee_components_new,
        params,
        show_toast: false,
      }),
    ).then((response) => {
      const data = response?.data?.data || response?.data || [];
      const list = Array.isArray(data) ? data : [];
      const options = list.map((ee) => ({
        value: String(ee?.ee_id),
        label: ee?.assembly_number ? String(ee.assembly_number) : String(ee?.ee_id),
      }));
      setEeComponentOptions(options);

      const zoneKey = isZonal ? selectedZoneName : "default";
      const rememberedEE = zoneEEHistoryRef.current[zoneKey];
      const hasRemembered = options.some((opt) => opt.value === rememberedEE);

      if (hasRemembered) {
        setSelectedEEId(rememberedEE);
      } else if (options.length > 0) {
        setSelectedEEId(options[0].value);
        if (zoneKey) {
          setZoneEEHistory((prev) => ({
            ...prev,
            [zoneKey]: options[0].value,
          }));
        }
      } else {
        setSelectedEEId("");
      }
    });
  }, [
    openSWModuleReplaceDialog,
    selectedProjectDetails,
    selectedSingleVariantDetails,
    selectedZoneName,
    viewArchived,
    title,
    dispatch,
  ]);

  const clearReplaceSWModuleFields = () => {
    setSelectedZoneName("");
    setSelectedEEId("");
    setZoneEEHistory({});
    setEeComponentOptions([]);
    setName("");
    setNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSummaryError("");
    setSelectedVfIds([]);
    setSupplierName([]);
    setSelectedKeywordIds([]);
    setDiscpError("");
    setSelectedVariantsManySW([]);
    setGithubLink("");
    setGithubLinkError("");
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };

  const isReplaceSWModuleDetailsValid = () => {
    if (title === "Draft Software Module") {
      const isDetailsValid = validateUpdateDraftSWComponent({
        name,
        nameError,
        version,
        versionError,
        description,
        descriptionError,
        summary,
        summaryError,
        supplierName,
        selectedVfIds,
        githubLink,
      });
      const isZoneValid =
        !selectedProjectDetails?.is_zonal || Boolean(selectedZoneName);
      const isEEValid = Boolean(selectedEEId);
      return isDetailsValid && isZoneValid && isEEValid;
    }

    return validateReplaceSWModuleDetails({
      name,
      nameError,
      version,
      versionError,
      description,
      descriptionError,
      summary,
      summaryError,
      supplierName,
      selectedVfIds,
      selectedKeywordIds,
      selectedSWModuleReplaceDetails,
      githubLink,
    });
  };

  const isReplaceSWModuleValid = () =>
    isReplaceSWModuleDetailsValid() && discp?.trim()?.length >= 3;

  const isUpdateDraftSWComponentValid = () =>
    validateUpdateDraftSWComponent({
      name,
      nameError,
      version,
      versionError,
      description,
      descriptionError,
      summary,
      summaryError,
      supplierName,
      selectedVfIds,
      githubLink,
    });

  const isUpdateAgainDraftSWComponentValid = () =>
    validateUpdateAgainDraftSWComponent({
      name,
      version,
      description,
      summary,
      supplierName,
      selectedVfIds,
      selectedKeywordIds,
      githubLink,
    });
  const handleCloseSWModuleReplace = () => {
    setOpenSWModuleReplaceDialog(false);

    clearReplaceSWModuleFields();

    setSelectedSWModuleReplaceDetails(null);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);
  };

  const handleReplaceClick = () => {
    setShowConfirmation(true); // Show confirmation dialog when update is clicked
  };

  const handleUpdateDraftClickSW = () => {
    const reqObj = {
      supplier_name: supplierName,
      keywords: selectedKeywordIds?.map(Number) || [],
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      save_as_draft: false,
      name: name?.trim(),
      ...(summary && { summary }),
      description: description,
      vf_ids: selectedVfIds,
      ...getGithubLinkPayload(githubLink),
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/swm/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft SWM Module",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() =>
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: Number(selectedSWModuleReplaceDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
            zone: selectedZone,
            archive: !!viewArchived,
          },
          show_toast: false,
        }),
      ),
    );
    handleCloseSWModuleReplace();
  };
  const handleUpdateAgainDraftClickSW = () => {
    const reqObj = {
      ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),
      keywords: selectedKeywordIds?.map(Number) || [],
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      ...(version ? { version: version?.trim() } : null),
      save_as_draft: true,
      name: name?.trim(),
      ...(summary && { summary }),
      ...(description ? { description: description } : null),
      ...(selectedVfIds?.length > 0 ? { vf_ids: selectedVfIds } : null),
      ...getGithubLinkPayload(githubLink),
      attributes: attributeFormData?.added || [],
    };
    dispatch(
      HandleApiActions({
        api_name: "/VLC/swm/update/draft",
        https_method: "POST",
        action_type: actionType.ADD_SW_MODULE_DRAFT,
        description: "Add in Save As Draft SWM Module",
        params: reqObj,
        show_toast: true,
      }),
    ).then(() =>
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: Number(selectedSWModuleReplaceDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
            zone: selectedZone,
            archive: !!viewArchived,
          },
          show_toast: false,
        }),
      ),
    );
    handleCloseSWModuleReplace();
  };

  const updateAttributes = async () => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];

    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];

    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    if (
      addedAttributes.length === 0 &&
      updatedAttributes.length === 0 &&
      deletedAttributes.length === 0
    ) {
      return;
    }

    const attributeResponse = await Promise.resolve(
      dispatch(
        HandleApiActions({
          api_name: "/VLC/attribute/swm/update",
          https_method: "PUT",
          params: {
            entity_id: Number(selectedSWModuleReplaceDetails?.swm_id),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: false,
        }),
      ),
    );

    if (attributeResponse?.error) {
      setDisableActionButton(false);
    }
  };

  const handlePostReplaceRedirect = async (details, willImptIntrOnly) => {
    if (willImptIntrOnly) {
      handleActionType2DialogOpen(details);
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_action_type,
            params: {
              current_time: new Date().toISOString(),
              project_id: Number(details?.project_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              ee_id: Number(details?.ee_id),
              swm_id: Number(details?.swm_id),
              is_dependency_linking: true,
            },
            show_toast: false,
          }),
        ),
      );
    } else {
      handleActionType1Dialogpen(details);
    }
  };

  const confirmReplaceAndRedirect = async ({
    selectedSWModuleReplaceDetails,
    willImptIntrOnly = false,
  }) => {
    if (!selectedSWModuleReplaceDetails?.swm_id) return;

    setDisableActionButton(true);
    await updateAttributes();

    const isDraftSWM = title === "Draft Software Module";
    const newEEId = selectedEEId ? Number(selectedEEId) : undefined;
    const isEEChanged =
      newEEId !== undefined &&
      newEEId !== Number(selectedSWModuleReplaceDetails?.ee_id);

    const reqObj = buildReplaceSWModulePayload({
      selectedSWModuleReplaceDetails,
      selectedSingleVariantDetails,
      selectedEEComponentDetails,
      supplierName,
      selectedKeywordIds,
      newEEId,
      isDraftSWM,
      isEEChanged,
      version,
      name,
      summary,
      description,
      selectedVfIds,
      attributeFormData,
      discp,
      githubLink,
    });

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      if (!response.error) {
        handleCloseSWModuleReplace();
        if (!isDraftSWM) {
          await handlePostReplaceRedirect(selectedSWModuleReplaceDetails, willImptIntrOnly);
        }
        getSWModulesList({ force: true });
      }
    } catch (error) {
      console.error("Failed to replace", error);
    } finally {
      setDisableActionButton(false);
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseSWModuleReplace();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_set_tree,
            params: {
              rule_set_id: ruleSetIdToUse,
            },
            show_toast: false,
          }),
        ),
      );

      if (response?.data) {
        setRuleSetTree(response.data);
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
      }
    } catch (error) {
      console.error("Replace dialog: Failed to fetch rule set questions", error);
    }
  };

  const replaceChangesWithoutStandardAndClose = async () => {
    await updateAttributes();
    const reqObj = {
      ...(supplierName?.length > 0 ? { supplier_name: supplierName } : null),
      keywords: selectedKeywordIds?.map(Number) || [],
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      name: name?.trim(),
      ...(summary && { summary }),
      description: description,
      vf_ids: selectedVfIds,
      will_it_impact: false,
      reason: discp,
      requires_homologation: false,
      ...getGithubLinkPayload(githubLink),
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (response?.data?.snow_resp) {
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }
      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.get_sw_modules,
            params: {
              project_id: Number(selectedSWModuleReplaceDetails?.project_id),
              ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              zone: selectedZone,
              archive: !!viewArchived,
            },
            show_toast: false,
          }),
        ),
      );
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const replaceChangesAndClose = async () => {
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation = lastNode?.data?.requires_homologation || false;
    if (selectedSWModuleReplaceDetails?.swm_id) {
      const reqObj = {
        supplier_name: supplierName,
        keywords: selectedKeywordIds?.map(Number) || [],
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variant_ids: selectedEEComponentDetails?.variant_ids,
        name: name?.trim(),
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        version: version?.trim(),
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
        ...getGithubLinkPayload(githubLink),
      };
      try {
        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.replace_sw_module,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
        }
        if (response?.data?.snow_resp) {
          setTimeout(() => {
            setTicketsDialogOpen(true);
          }, 200);
        }
        await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.get_sw_modules,
              params: {
                project_id: Number(selectedSWModuleReplaceDetails?.project_id),
                ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
                variant_id: Number(selectedSingleVariantDetails?.variant_id),
                zone: selectedZone,
                archive: !!viewArchived,
              },
              show_toast: false,
            }),
          ),
        );
      } catch (error) {
        console.error("Failed to replace", error);
      }
    }
  };


  const handleActionType1Dialogpen = (selectedSWModuleReplaceDetails) => {
    setSelectedSWM(selectedSWModuleReplaceDetails);
    setActionType1DialogOpen(true);
  };
  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogOpen = (selectedSWModuleReplaceDetails) => {
    setSelectedSWM(selectedSWModuleReplaceDetails);
    setActionType2DialogOpen(true);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  useEffect(() => {
    if (
      openSWModuleReplaceDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsManySW((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
  }, [openSWModuleReplaceDialog, selectedSingleVariantDetails]);
  const replaceComponentAndCheckForDecisionMatrix = async () => {
    try {
      const ticketDetails =
        createTicketData && Object.keys(createTicketData).length > 0
          ? createTicketData
          : null;
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.re_create_ticket,
            params: {
              execution_id: executionId,
              entity_type: "swm",
              entity_id: Number(selectedSWModuleReplaceDetails?.swm_id),
              reason: discp,
              ...(ticketDetails ? { details: ticketDetails } : {}),
            },
            show_toast: true,
          }),
        ),
      );

      handleCloseSWModuleReplace();
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const loadDecisionMatrixContext = async () => {
    const standardsRes = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...ruleEngineActions.decision_matrix_get_context,
          params: {
            component_type: "SWM",
            component_id: selectedSWModuleReplaceDetails.swm_id,
            project_id: selectedProjectDetails.project_id,
            variant_id: selectedSingleVariantDetails.variant_id,
          },
          show_toast: false,
        }),
      ),
    );
    setOpenSWModuleReplaceDialog(false);
    setShowConfirmation(false); // Reset confirmation on close
    setShowIntersectionImpactPrompt(false);

    const contextData = standardsRes?.data;

    const isValid = isDecisionMatrixContextValid(contextData);

    if (!isValid) {
      console.warn(
        "Invalid decision matrix context, cannot proceed:",
        contextData,
      );
      setDecisionMatrixContext(null);
      setShowDecisionMatrix(false);
      return;
    }

    setDecisionMatrixContext(standardsRes?.data);

    setShowDecisionMatrix(true);
  };

  const handleExecuteWorkflow = async ({ mode, payload }) => {
    // Replace-dialog workflow execution
    if (mode === "START") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_start,
            params: {
              workflowId: payload.workflowId,
              version: payload.version,
            },
            show_toast: false,
          }),
        ),
      );

      const { executionId: newExecutionId } = res ?? {};
      setExecutionId(newExecutionId);

      return res;
    }

    if (mode === "COMPLETE") {
      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.wfe_execution_node_complete,
            params: {
              executionId: payload.executionId,
              nodeId: payload.nodeId,
              input: payload.input,
            },
            show_toast: false,
          }),
        ),
      );

      return res;
    }

    throw new Error(`Unsupported execution mode: ${mode}`);
  };

  const handleViewExecution = (executionId) => {
  };

  const handelGetSimilerTickets = async ({ standard_name } = {}) => {
    try {
      const reCreateTicketParams = {
        execution_id: executionId,
        entity_type: "swm",
        entity_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        ...(standard_name ? { standard_name } : {}),
      };

      const res = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_similar_tickets,
            params: reCreateTicketParams,
            show_toast: true,
          }),
        ),
      );

      setSimilarTickets(res?.data || []);
      setCreateTicketData(res?.data || []);
      return res;
    } catch (error) {
      console.error("Failed to update", error);
    }
  };

  const handelGetAiSuggestion = (ticket) => {
    setSelectedEntity({
      type: "INTERSECTION",
      id: ticket?.intr_id,
      targetEntityTypes: ["ARTIFACT"],
      topK: 20,
      minConfidence: 0.6,
      description: "SW Module - Replace",
    });
    setOpenAiPanel(true);
    setSelectedTicket(ticket);
  };

  const handelCloseDecisionMatrix = () => {
    // Replace-dialog: reset all decision matrix state
    setShowDecisionMatrix(false);
    setDecisionMatrixContext(null);
    setRulePath([]);
    setSimilarTickets([]);
    setSelectedEntity(null);
    setSelectedTicket(null);
    setCreateTicketData({});
    setDisabledStandards([]);
    setExecutionId(null);
  };
  const onDisabledVal = getReplaceDialogDisabledState({
    title,
    swmTabValue,
    isReplaceDetailsValid: isReplaceSWModuleDetailsValid(),
    isReplaceValid: isReplaceSWModuleValid(),
    isUpdateAgainDraftValid: isUpdateAgainDraftSWComponentValid(),
    isUpdateDraftValid: isUpdateDraftSWComponentValid(),
  });

  const {
    successButtonName: successButtonNameVal,
    rejectButtonName: rejectButtonNameVal,
    rejectDisabled,
    rejectButtonType,
    onClickReject,
    otherButtonName,
  } = getReplaceDialogButtonConfig({
    title,
    swmTabValue,
    isUpdateAgainDraftValid: isUpdateAgainDraftSWComponentValid(),
    handleClose: handleCloseSWModuleReplace,
    handleUpdateAgainDraft: handleUpdateAgainDraftClickSW,
  });

  const handleSuccessClick = () => {
    if (swmTabValue === 0) {
      setSwmTabValue(1);
      return;
    }
    if (title === "Draft Software Module") {
      void confirmReplaceAndRedirect({
        selectedSWModuleReplaceDetails,
        willImptIntrOnly: false,
      });
    } else if (title === "Replace Software Module") {
      handleReplaceClick();
    } else {
      handleUpdateDraftClickSW();
    }
  };

  return (
    <Box>
      <CustomDialogBoxV2
        open={openSWModuleReplaceDialog}
        onClose={handleCloseSWModuleReplace}
        title={title}
        onDisabled={onDisabledVal}
        successButtonName={successButtonNameVal}
        rejectButtonName={rejectButtonNameVal}
        rejectDisabled={rejectDisabled}
        rejectButtonType={rejectButtonType}
        onClickSuccess={handleSuccessClick}
        onClickReject={onClickReject}
        actionButtonStyle={"three"}
        otherButtonName={otherButtonName}
        onOtherButtonClick={() => setSwmTabValue(0)}
        disabled={false}
        typeForThree={"cancel"}
        otherButtonPlacement={"left"}
      >
        <CustomTabsV2
          defaultTab={swmTabValue}
          value={swmTabValue}
          onTabChange={(newValue) => setSwmTabValue(newValue)}
          tabs={[
            {
              name: "Details",
              content: (
                <Box sx={{ width: "450px" }}>
                  <Stack spacing={2} mb={2}>
                    {selectedProjectDetails?.is_zonal &&
                      title !== "Replace Software Module" &&
                      title !== "Add Draft Software Module" && (
                        <CustomSelectV2
                          size={"small"}
                          label={"Select Zone"}
                          required={true}
                          optionsList={
                            selectedProjectDetails?.project_zones?.map((zone) => ({
                              value: zone?.zone_name,
                              label: zone?.zone_name,
                            })) || []
                          }
                          selectedValue={selectedZoneName || " "}
                          handleOnChange={(e) => {
                            setSelectedZoneName(e.target.value);
                            setSelectedEEId("");
                          }}
                        />
                      )}
                    {title !== "Replace Software Module" &&
                      title !== "Add Draft Software Module" && (
                        <CustomSelectV2
                          size={"small"}
                          label={"Select Parent"}
                          required={true}
                          optionsList={eeComponentOptions}
                          selectedValue={selectedEEId || " "}
                          handleOnChange={(e) => {
                            const val = e.target.value;
                            setSelectedEEId(val);
                            const zoneKey = selectedProjectDetails?.is_zonal
                              ? selectedZoneName
                              : "default";
                            if (zoneKey) {
                              setZoneEEHistory((prev) => ({
                                ...prev,
                                [zoneKey]: val,
                              }));
                            }
                          }}
                        />
                      )}
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Number"}
                      required={true}
                      value={name}
                      setValue={(value) =>
                        value?.length <= 40 && setName(value)
                      }

                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Version"}
                      required={true}
                      type={"text"}
                      fldType={"textR"}
                      value={version}
                      setValue={(value) =>
                        value?.length <= 40 && setVersion(value)
                      }

                      maxLength={40}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Description"}
                      required={true}
                      value={description}
                      setValue={(value) =>
                        value?.length <= 500 && setDescription(value)
                      }

                      maxLength={500}
                    />
                    <CustomFormTextFieldV2
                      size={"small"}
                      label={"Summary"}

                      value={summary}
                      setValue={(value) =>
                        value?.length <= 250 && setSummary(value)
                      }

                      maxLength={250}
                    />

                    <MultiselectConnectorV2
                      multiple
                      selectedValues={supplierName || []}
                      setSelectedValues={setSupplierName}
                      label={"Select Supplier Name"}
                      optionsList={supplierOptionsList}
                      placeholder={false}
                    />

                    <MultiselectConnectorV2
                      multiple
                      selectedValues={selectedKeywordIds || []}
                      setSelectedValues={setSelectedKeywordIds}
                      label={"Keywords"}
                      optionsList={keywordOptions}
                      placeholder={false}
                    />


                    <MultiselectConnectorV2
                      required
                      size={"small"}
                      label={"Select Vehicle Functions"}
                      optionsList={selectedSingleVariantDetails?.vehicle_function_details?.map(
                        (el) => {
                          return {
                            value: el.id,
                            label: `${el.abbreviation} - ${el.name}`,
                          };
                        },
                      )}
                      selectedValues={selectedVfIds || " "}
                      setSelectedValues={setSelectedVfIds}
                      placeholder={false}
                    />
                    <CustomFormTextFieldV2
                      size="small"
                      label="GitHub Link"
                      placeholder="https://github.com/username/repo"
                      value={githubLink}
                      valueError={githubLinkError}
                      setValueError={setGithubLinkError}
                      setValue={(value) => {
                        if (value?.length <= 100) {
                          setGithubLink(value);
                          setGithubLinkError(getGithubLinkError(value));
                        }
                      }}
                      maxLength={100}
                    />

                    {title === "Add Draft Software Module" ? (
                      <MultiselectConnectorV2
                        disabled={true}
                        size={"small"}
                        multiple
                        selectedValues={selectedVariantsManySW || " "}
                        setSelectedValues={setSelectedVariantsManySW}
                        label={"Select Variants"}
                        optionsList={variantOptionList}
                        placeholder={false}
                        immutableValues={
                          selectedSingleVariantDetails?.variant_id
                            ? [selectedSingleVariantDetails?.variant_id]
                            : []
                        }
                      />
                    ) : null}


                  </Stack>
                </Box>
              ),
            },
            {
              name: "Attribute",
              content: (
                <Stack
                  spacing={2}
                  mb={2}
                  justifyContent={"center"}
                  sx={{ width: "450px" }}
                >
                  <AttributesForm
                    type={"EDIT"}
                    attributeFromApi={
                      selectedSWModuleReplaceDetails?.attributes || []
                    }
                    getFormData={handleAttributeFormData}
                    reason={discp}
                    setReason={setDiscp}
                    reasonError={discpError}
                    showReason={title === "Replace Software Module" || title === "Draft Software Module"}
                  />
                </Stack>
              ),
            },
          ]}
        />
      </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={showConfirmation}
          title={"Action"}
          onClose={() => {
            setShowConfirmation(false);
          }}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  setShowConfirmation(false);

                  setTimeout(() => {
                    setShowIntersectionImpactPrompt(true);
                  }, 200);
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={() =>
                  confirmReplaceAndRedirect({
                    selectedSWModuleReplaceDetails,
                  })
                }
                disabled={disableActionButton}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Will it impact the existing dependencies?
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={showIntersectionImpactPrompt}
          onClose={() => {
            setShowIntersectionImpactPrompt(false);
          }}
          title={"Action"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <CustomButtonV2
                size="small"
                type="cancel"
                btnName="No"
                handleOnClick={() => {
                  void loadDecisionMatrixContext();
                  void replaceChangesWithoutStandardAndClose();
                }}
              />
              <CustomButtonV2
                size="small"
                type="submit"
                btnName="Yes"
                handleOnClick={() =>
                  void confirmReplaceAndRedirect({
                    selectedSWModuleReplaceDetails,
                    willImptIntrOnly: true,
                  })
                }
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography sx={{ ...componentStyle.font.text }}>
              Will it impact the existing intersection?
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${selectedSWM?.swm_id}&ee_id=${selectedEEComponentDetails?.ee_id
              }&selectedZone=${selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }} // Trigger the update process
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>



        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${selectedEEComponentDetails?.ee_id
              }&zone=${selectedZone?.zone_position_name}&swm_id=${selectedSWM?.swm_id
              }&tab=${3}`,
            );
            setActionType2DialogOpen(false);
          }} // Trigger the update process
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>




        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets?tab=${1}`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>


        <CustomDialogBoxV2
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
          title="Select Rule Set"
          onClickSuccess={async () => {
            await viewRuleSetQuestions(selectedRuleSet);
            setShowRuleSetListDialog(false);
          }}
          onDisabled={!selectedRuleSet}
          onClickReject={handleRuleSetListDialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Confirm"}
          rejectButtonName={"Cancel"}
        >
          <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
            <CustomSelectV2
              size={"small"}
              label={"Select Rule Set"}
              required={true}
              optionsList={
                ruleSetsList?.map((rs) => ({
                  value: rs?.rule_set_id || rs?.id,
                  label: rs?.name,
                })) || []
              }
              selectedValue={selectedRuleSet || " "}
              handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
            />
          </Stack>
        </CustomDialogBoxV2>



        <CustomDialogBoxV2
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
          title={`Rule Set: ${ruleSetTree?.name || ""}`}
          contentSx={{ minHeight: "350px", width: "450px" }}

        >
          <DialogContent>
            <Stack spacing={2} sx={{}}>
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  const itemKey = entry.node?.id || entry.node?.rule_set_id || `rule-path-${idx}`;
                  return (
                    <Box
                      key={itemKey}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath?.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {isCategory ? (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box>
                              <CustomButtonV2
                                btnName={"Yes"}
                                handleOnClick={() => handleAnswerAt(idx, "yes")}
                                type={entry.answer === "yes" ? "submit" : "cancel"}
                              />
                            </Box>
                            <Box>
                              <CustomButtonV2
                                btnName={"No"}
                                handleOnClick={() => handleAnswerAt(idx, "no")}
                                type={entry.answer === "no" ? "submit" : "cancel"}
                              />
                            </Box>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {
                  rulePath?.length > 0 &&
                    rulePath[rulePath?.length - 1]?.node?.type === "category" ? (
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="center"
                      mt={2}
                    >
                      <CustomButtonV2
                        type="submit"
                        btnName={"Finish"}
                        handleOnClick={() => {
                          void replaceChangesAndClose();
                          setShowRuleSetQuestionDialog(false);
                          setRuleSetTree(null);
                          setRulePath([]);
                          setSelectedRuleSet("");
                          handleCloseSWModuleReplace();
                        }}
                      />
                    </Stack>
                  ) : null

                }
              </Stack>
            </Stack>
          </DialogContent>
        </CustomDialogBoxV2>
      <Dialog
        open={showDecisionMatrix}
        onClose={handelCloseDecisionMatrix}
        fullScreen
        slotProps={{
          paper: {
            sx: {
              width: "100vw",
              height: "100vh",
              margin: 0,
              borderRadius: 0,
            },
          },
        }}
      >
        <AppBar
          position="sticky"
          sx={{
            backgroundColor: "#26890D",
          }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Decision Matrix
            </Typography>
            <IconButton color="inherit" onClick={handelCloseDecisionMatrix}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flex: 1,
            height: "calc(100vh - 64px)",
            overflow: "auto",
            p: 2,
          }}
        >
          {showDecisionMatrix && (
            <DecisionMatrixPanel
              context={decisionMatrixContext}
              key={showDecisionMatrix ? "open" : "closed"}
              onExecuteWorkflow={handleExecuteWorkflow}
              onViewExecution={handleViewExecution}
              onClose={handelCloseDecisionMatrix}
              onDecisionMatrixAction={replaceComponentAndCheckForDecisionMatrix}
              handelGetSimilerTickets={handelGetSimilerTickets}
              similarTickets={similarTickets}
              setSimilarTickets={setSimilarTickets}
              entityDetails={selectedEntity}
              handelGetAiSuggestion={handelGetAiSuggestion}
              selectedTicket={selectedTicket}
              setSelectedTicket={setSelectedTicket}
              openAiPanel={openAiPanel}
              setOpenAiPanel={setOpenAiPanel}
              setCreateTicketData={setCreateTicketData}
              disabledStandards={disabledStandards}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}

VlcSWModuleReplaceDialog.propTypes = {
  openSWModuleReplaceDialog: PropTypes.bool,
  setOpenSWModuleReplaceDialog: PropTypes.func,
  viewArchived: PropTypes.bool,
  selectedSWModuleReplaceDetails: PropTypes.shape({
    swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    version: PropTypes.string,
    description: PropTypes.string,
    summary: PropTypes.string,
    supplier_name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    keywords: PropTypes.array,
    vf_ids: PropTypes.array,
    variant_ids: PropTypes.array,
    attributes: PropTypes.array,
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    git_link: PropTypes.string,
    meta_details: PropTypes.shape({
      linked_meta_link: PropTypes.string,
    }),
    zone_position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zone_details: PropTypes.shape({
      zone_name: PropTypes.string,
      zone_position_name: PropTypes.string,
    }),
  }),
  setSelectedSWModuleReplaceDetails: PropTypes.func,
  vfOptionsList: PropTypes.array,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  selectedZone: PropTypes.shape({
    zone_position_name: PropTypes.string,
  }),
  selectedEEComponentDetails: PropTypes.shape({
    ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    variant_ids: PropTypes.array,
    zone_details: PropTypes.shape({
      zone_name: PropTypes.string,
      zone_position_name: PropTypes.string,
    }),
  }),
  getSWModulesList: PropTypes.func,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parent_project_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    vehicle_function_details: PropTypes.array,
    is_user_project_owner: PropTypes.bool,
    is_zonal: PropTypes.bool,
    project_zones: PropTypes.arrayOf(PropTypes.any),
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vehicle_function_details: PropTypes.array,
    parent_variant_details: PropTypes.shape({
      variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  variantOptionList: PropTypes.array,
  title: PropTypes.string,
  supplierOptionsList: PropTypes.array,
};

const styles = {
  closeButton: {
    color: "black",
    position: "absolute",
    top: { xs: "2px", sm: "4px" },
    right: { xs: "4px", sm: "8px" },
  },
  swmCompMainContainer: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
    height: "450px",
    minHeight: "450px",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "stretch",
    boxSizing: "border-box",
    marginBottom: { xs: "8px", sm: "10px", md: "15px" },
  },
  card: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    py: { xs: 0.5, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
  },
  cardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  cardItems: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
    border: "1px solid lightgrey",
  },
  cardItemsTxtContainer: {
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: 0.5,
  },
  noDataAvailableTxt: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  customTextFieldN: {
    width: { xs: "140px", sm: "180px", md: "220px" },
    fontSize: { xs: "0.85rem", sm: "0.95rem" },
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: "1px solid lightgrey",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  edRpIconsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 0.5, sm: 1 },
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
};
